import { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Plus, Search, Edit2, Trash2, Upload, Filter, Eye, Trash, RotateCcw, ArrowLeft, Receipt, Download } from 'lucide-react';
import { licenseApi, companyApi, License, Company } from '../services/api.ts';
import { Building2, ChevronDown, ChevronRight, FileText } from 'lucide-react';
import moment from 'moment';
import { parseExcelFile, convertToLicenseData } from '../services/importService.ts';
import { SimpleBarChart, SimplePieChart } from '../components/ChartHelpers.tsx';
import ViewToggle, { ViewMode } from '../components/ViewToggle.tsx';

type RenewalStage = 'not_yet' | 'eligible' | 'overdue' | 'unknown';
interface RenewalAvailability {
  stage: RenewalStage;
  label: string;
  days: number;
  rowBg: string;
  textColor: string;
  badgeBg: string;
  badgeText: string;
}

const ONE_DAY_MS = 86400000;

const getPstDateString = () => {
  const now = new Date();
  const pstFormatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Los_Angeles',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const parts = pstFormatter.formatToParts(now);
  const year = parts.find(p => p.type === 'year')?.value;
  const month = parts.find(p => p.type === 'month')?.value;
  const day = parts.find(p => p.type === 'day')?.value;
  return `${year}-${month}-${day}`;
};

const getDaysRemaining = (expirationDate: string | undefined | null) => {
  if (!expirationDate || expirationDate.trim() === '') return 999;
  const todayPst = getPstDateString();
  const todayParts = todayPst.split('-').map(Number);
  const expireStr = expirationDate.split('T')[0].slice(0, 10);
  const expireParts = expireStr.split('-').map(Number);
  if (expireParts.some(isNaN)) return 999;
  const todayUtcMs = Date.UTC(todayParts[0], todayParts[1] - 1, todayParts[2]);
  const expireUtcMs = Date.UTC(expireParts[0], expireParts[1] - 1, expireParts[2]);
  return Math.round((expireUtcMs - todayUtcMs) / ONE_DAY_MS);
};

const dedupeLicensesById = (licenses: License[]): License[] => {
  if (!Array.isArray(licenses)) return [];
  const seen = new Map<number, License>();
  for (const lic of licenses) {
    if (!lic || typeof lic.id !== 'number' || !Number.isFinite(lic.id) || !Number.isInteger(lic.id) || lic.id <= 0) {
      continue;
    }
    if (!seen.has(lic.id)) seen.set(lic.id, lic);
  }
  return Array.from(seen.values());
};

const findDuplicateIds = (list: License[]): Set<number> => {
  const count = new Map<number, number>();
  for (const lic of list) {
    const id = lic?.id;
    if (typeof id === 'number' && Number.isFinite(id) && Number.isInteger(id) && id > 0) {
      count.set(id, (count.get(id) ?? 0) + 1);
    }
  }
  const dup = new Set<number>();
  count.forEach((c, id) => { if (c > 1) dup.add(id); });
  return dup;
};

const isValidPositiveInteger = (n: unknown): n is number => {
  return typeof n === 'number' && Number.isFinite(n) && Number.isInteger(n) && n > 0;
};

// ============================================================
// 欄位篩選共用邏輯（列表實際篩選 與 篩選彈窗 faceted 計數 共用同一份口徑）
// ============================================================
type DateFilterOp = '' | 'eq' | 'neq' | 'after' | 'before';
interface DateFilter { op: DateFilterOp; value: string; }
type FacetCol = 'company_name' | 'location' | 'certificate_name' | 'category' | 'status';
interface LicenseColumnFilters {
  company_name: string[];
  location: string[];
  certificate_name: string[];
  category: string[];
  status: string[];
}

const ymdOf = (d: string | undefined | null): string | null => {
  if (!d) return null;
  const s = String(d).split('T')[0].slice(0, 10);
  if (!s || s === '0000-00-00' || s === '1900-01-01') return null;
  return s;
};

interface ApplyLicenseFiltersArgs {
  searchTerm: string;
  columnFilters: LicenseColumnFilters;
  expirationDateFilter: DateFilter;
  renewalDateFilter: DateFilter;
  // facet 計算用：計算該欄選項數量時，忽略該欄自身已選條件（一環扣一環）
  skipColumn?: FacetCol;
  // status facet 需要顯示 Cancelled/Terminated，故不套用預設排除
  skipDefaultStatusExclusion?: boolean;
}

function applyLicenseFilters(source: License[], args: ApplyLicenseFiltersArgs): License[] {
  const { searchTerm, columnFilters: cf, expirationDateFilter, renewalDateFilter, skipColumn, skipDefaultStatusExclusion } = args;
  let result = [...source];

  if (searchTerm) {
    const term = searchTerm.toLowerCase();
    result = result.filter(license =>
      (license.company_name || '').toLowerCase().includes(term) ||
      (license.certificate_name || '').toLowerCase().includes(term) ||
      (license.license_no || '').toLowerCase().includes(term) ||
      (license.category || '').toLowerCase().includes(term) ||
      (license.location || '').toLowerCase().includes(term)
    );
  }
  const hasSearch = !!searchTerm;

  // 預設排除 Cancelled / Terminated（與列表顯示一致；status facet 除外）
  if (cf.status.length === 0 && !hasSearch && !skipDefaultStatusExclusion) {
    result = result.filter(license => {
      const s = (license.status || '').toLowerCase();
      return s !== 'cancelled' && s !== 'terminated';
    });
  }

  if (skipColumn !== 'company_name' && cf.company_name.length > 0) {
    result = result.filter(l => cf.company_name.includes(l.company_name || ''));
  }
  if (skipColumn !== 'location' && cf.location.length > 0) {
    result = result.filter(l => cf.location.includes(l.location || ''));
  }
  if (skipColumn !== 'certificate_name' && cf.certificate_name.length > 0) {
    result = result.filter(l => cf.certificate_name.includes(l.certificate_name || ''));
  }
  if (skipColumn !== 'category' && cf.category.length > 0) {
    result = result.filter(l => {
      const cats = (l.category || '').split(',').map(s => s.trim()).filter(Boolean);
      return cats.some(c => cf.category.includes(c));
    });
  }
  if (skipColumn !== 'status' && cf.status.length > 0) {
    result = result.filter(l => cf.status.includes(l.status || ''));
  }

  const applyDateFilter = (rows: License[], filter: DateFilter, getValue: (l: License) => string | undefined | null): License[] => {
    if (!filter.op || filter.op === '') return rows;
    const target = filter.value?.split('T')[0]?.slice(0, 10) || '';
    if (!target) return rows; // 有條件但沒選日期 → 忽略該篩選
    return rows.filter(l => {
      const row = ymdOf(getValue(l));
      if (!row) return false;
      switch (filter.op) {
        case 'eq': return row === target;
        case 'neq': return row !== target;
        case 'after': return row > target;
        case 'before': return row < target;
        default: return true;
      }
    });
  };
  result = applyDateFilter(result, expirationDateFilter, l => l.expiration_date);
  result = applyDateFilter(result, renewalDateFilter, l => l.renewal_date);

  return result;
}

const CANONICAL_LICENSE_STATUSES: string[] = [
  'Active',
  'Renewing',
  'Applying',
  'Cancelling',
  'Cancelled',
  '待Cancel',
  '待變更',
  'HOLD',
  '待Apply',
  'Deleted'
];

const toUtcMs = (d: string | undefined | null): number | null => {
  if (!d || d.trim() === '') return null;
  const s = d.split('T')[0].slice(0, 10);
  const p = s.split('-').map(Number);
  if (p.some(isNaN)) return null;
  return Date.UTC(p[0], p[1] - 1, p[2]);
};

const getRenewalAvailability = (
  renewalDate: string | undefined | null,
  expirationDate: string | undefined | null
): RenewalAvailability => {
  const todayPst = getPstDateString();
  const todayParts = todayPst.split('-').map(Number);
  const todayUtcMs = Date.UTC(todayParts[0], todayParts[1] - 1, todayParts[2]);

  const expMs = toUtcMs(expirationDate);
  const rawRenewalMs = toUtcMs(renewalDate);

  if (!expMs && rawRenewalMs === null) {
    return {
      stage: 'unknown',
      label: '-',
      days: 0,
      rowBg: '',
      textColor: 'text-slate-500',
      badgeBg: 'bg-slate-100',
      badgeText: 'text-slate-700',
    };
  }

  if (expMs && todayUtcMs > expMs) {
    const daysOverdue = Math.abs(Math.round((expMs - todayUtcMs) / ONE_DAY_MS));
    return {
      stage: 'overdue',
      label: 'Overdue',
      days: daysOverdue,
      rowBg: 'bg-red-50 hover:bg-red-100',
      textColor: 'text-red-600 font-bold',
      badgeBg: 'bg-red-100',
      badgeText: 'text-red-800',
    };
  }

  if (rawRenewalMs !== null && todayUtcMs < rawRenewalMs) {
    const daysUntilOpen = Math.abs(Math.round((rawRenewalMs - todayUtcMs) / ONE_DAY_MS));
    return {
      stage: 'not_yet',
      label: 'Renewal not yet available',
      days: daysUntilOpen,
      rowBg: 'bg-sky-50 hover:bg-sky-100',
      textColor: 'text-sky-600 font-semibold',
      badgeBg: 'bg-sky-100',
      badgeText: 'text-sky-800',
    };
  }

  const daysLeft = expMs ? Math.round((expMs - todayUtcMs) / ONE_DAY_MS) : 999;

  let rowBg = 'hover:bg-slate-50';
  let textColor = 'text-slate-600';
  let badgeBg = 'bg-green-100';
  let badgeText = 'text-green-800';

  if (expMs) {
    if (daysLeft <= 30) {
      rowBg = 'bg-yellow-50 hover:bg-yellow-100';
      textColor = 'text-red-600 font-semibold';
      badgeBg = 'bg-yellow-100';
      badgeText = 'text-yellow-800';
    } else if (daysLeft <= 60) {
      rowBg = 'bg-yellow-50 hover:bg-yellow-100';
      textColor = 'text-yellow-600 font-semibold';
      badgeBg = 'bg-yellow-100';
      badgeText = 'text-yellow-800';
    }
  }

  return {
    stage: 'eligible',
    label: rawRenewalMs !== null ? 'Eligible for renewal' : expMs ? `${daysLeft} days left` : '-',
    days: daysLeft === 999 ? 0 : daysLeft,
    rowBg,
    textColor,
    badgeBg,
    badgeText,
  };
};

export default function LicenseList() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [licenses, setLicenses] = useState<License[]>([]);
  const [filteredLicenses, setFilteredLicenses] = useState<License[]>([]);
  const [allCompanies, setAllCompanies] = useState<Company[]>([]);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [expandedCompanies, setExpandedCompanies] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [gaFilters, setGaFilters] = useState<{
    groups: string[];
    companies: string[];
    states: string[];
    categories: string[];
  }>({ groups: [], companies: [], states: [], categories: [] });
  const [activeGaFilter, setActiveGaFilter] = useState<string | null>(null);
  const [gaFilterSearch, setGaFilterSearch] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [viewingLicense, setViewingLicense] = useState<License | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [showTrash, setShowTrash] = useState(false);
  const [deletedLicenses, setDeletedLicenses] = useState<License[]>([]);
  const [renewalDaysFilter, setRenewalDaysFilter] = useState<number>(30);
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const params = new URLSearchParams(window.location.search);
    const v = params.get('view');
    if (v === 'report') return 'report';
    if (v === 'renewal') return 'renewal';
    return 'list';
  });

  // === Column filters (header popovers, like CompanyList pattern) ===
  const EMPTY_DATE_FILTER: DateFilter = { op: '', value: '' };
  const [columnFilters, setColumnFilters] = useState<{
    company_name: string[];
    location: string[];
    certificate_name: string[];
    category: string[];
    status: string[];
  }>({
    company_name: [],
    location: [],
    certificate_name: [],
    category: [],
    status: [],
  });
  const [expirationDateFilter, setExpirationDateFilter] = useState<DateFilter>(EMPTY_DATE_FILTER);
  const [renewalDateFilter, setRenewalDateFilter] = useState<DateFilter>(EMPTY_DATE_FILTER);
  const [activeFilterCol, setActiveFilterCol] = useState<string | null>(null);
  const [filterSearch, setFilterSearch] = useState<Record<string, string>>({});
  const activeFilterColRef = useRef<HTMLDivElement>(null);
  const gaFilterRef = useRef<HTMLDivElement>(null);
  const DATE_FILTER_OPS: { value: DateFilterOp; label: string; needsDate: boolean }[] = [
    { value: '', label: 'All', needsDate: false },
    { value: 'eq', label: 'Equals (=)', needsDate: true },
    { value: 'neq', label: 'Not equals (≠)', needsDate: true },
    { value: 'after', label: 'After (>)', needsDate: true },
    { value: 'before', label: 'Before (<)', needsDate: true },
  ];
  const isDateFilterActive = (f: DateFilter): boolean => !!f.op && f.op !== '';

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const v = params.get('view');
    const target: ViewMode = v === 'report' ? 'report' : v === 'renewal' ? 'renewal' : 'list';
    if (target !== viewMode) setViewMode(target);
  }, [location.search]);
  
  // 分页状态
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const LICENSE_STATE_KEY = 'licenseListState';
  const LICENSE_FROM_PAGE_KEY = 'licenseList_fromPage';

  const saveLicenseStateRef = useRef<() => void>(() => {});
  const setLicenseFromPageRef = useRef<() => void>(() => {});
  const restoredRef = useRef(false);
  const restoringRef = useRef(false);

  const saveLicenseState = () => {
    const state = {
      searchTerm,
      columnFilters,
      expirationDateFilter,
      renewalDateFilter,
      currentPage,
      pageSize,
      viewMode,
      renewalDaysFilter,
    };
    sessionStorage.setItem(LICENSE_STATE_KEY, JSON.stringify(state));
  };
  saveLicenseStateRef.current = saveLicenseState;

  const setLicenseFromPage = () => {
    const currentPath = location.pathname + location.search;
    sessionStorage.setItem(LICENSE_FROM_PAGE_KEY, currentPath);
  };
  setLicenseFromPageRef.current = setLicenseFromPage;

  const restoreLicenseState = () => {
    if (restoredRef.current) return;
    const saved = sessionStorage.getItem(LICENSE_STATE_KEY);
    if (!saved) return;
    try {
      const s = JSON.parse(saved);
      restoringRef.current = true;
      if (s.searchTerm !== undefined) setSearchTerm(s.searchTerm);
      if (s.columnFilters) setColumnFilters({
        company_name: s.columnFilters.company_name || [],
        location: s.columnFilters.location || [],
        certificate_name: s.columnFilters.certificate_name || [],
        category: s.columnFilters.category || [],
        status: s.columnFilters.status || [],
      });
      if (s.expirationDateFilter) setExpirationDateFilter(s.expirationDateFilter);
      if (s.renewalDateFilter) setRenewalDateFilter(s.renewalDateFilter);
      if (s.currentPage) setCurrentPage(s.currentPage);
      if (s.pageSize) setPageSize(s.pageSize);
      if (s.viewMode) setViewMode(s.viewMode);
      if (s.renewalDaysFilter !== undefined) setRenewalDaysFilter(s.renewalDaysFilter);
      restoredRef.current = true;
      setTimeout(() => { restoringRef.current = false; }, 100);
    } catch {}
  };

  useEffect(() => {
    const handleDocMouseDown = (e: MouseEvent) => {
      const t = e.target as Node;
      const path = e.composedPath?.() as Node[] | undefined;
      const isInside = (ref: React.RefObject<HTMLDivElement>) => {
        const node = ref.current;
        if (!node) return false;
        if (node.contains(t)) return true;
        if (path && path.indexOf(node as unknown as EventTarget) !== -1) return true;
        return false;
      };
      if (activeFilterCol && !isInside(activeFilterColRef)) setActiveFilterCol(null);
      if (activeGaFilter && !isInside(gaFilterRef)) setActiveGaFilter(null);
    };
    const handleDocKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setActiveFilterCol(null);
        setActiveGaFilter(null);
      }
    };
    document.addEventListener('mousedown', handleDocMouseDown, true);
    document.addEventListener('keydown', handleDocKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleDocMouseDown, true);
      document.removeEventListener('keydown', handleDocKeyDown);
    };
  }, [activeFilterCol, activeGaFilter]);

  useEffect(() => {
    restoredRef.current = false;
    restoreLicenseState();
    sessionStorage.setItem(LICENSE_FROM_PAGE_KEY, location.pathname + location.search);
    fetchLicenses();
    fetchCompanies();
    return () => { saveLicenseStateRef.current(); };
  }, [location.pathname, location.search, refreshKey]);
  
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.has('refresh')) {
      setRefreshKey(prev => prev + 1);
      params.delete('refresh');
      window.history.replaceState({}, document.title, '/license' + (params.toString() ? '?' + params.toString() : ''));
    }
  }, [location.search]);

  useEffect(() => {
    // 篩選邏輯統一由 applyLicenseFilters 處理（與彈窗 faceted 計數同口徑）
    let result = applyLicenseFilters(licenses, { searchTerm, columnFilters, expirationDateFilter, renewalDateFilter });

    // —— 统一排序（License List Data View 默认排序）
    //   1. Overdue (days < 0)          —— ASC：最负优先（逾期最久的在前）
    //   2. Expiring (0 ≤ days ≤ 90)   —— ASC：最近到期在前
    //   3. Active   (days > 90)       —— ASC：最近到期在前
    // 每 Bucket 内再按 days ASC 二级排序
    const bucketOrder = (days: number): number => {
      if (days === 999) return 9; // 无日期数据
      if (days < 0) return 1;     // Overdue
      if (days <= 90) return 2;   // Expiring
      return 3;                   // Active
    };
    result.sort((a, b) => {
      const da = getDaysRemaining(a.expiration_date);
      const db = getDaysRemaining(b.expiration_date);
      const bo = bucketOrder(da) - bucketOrder(db);
      if (bo !== 0) return bo;
      // 同 Bucket 内按 days ASC
      if (da !== db) return da - db;
      // days 相同，按货仓排序做三级排序
      const parseLocation = (loc: string) => {
        const location = (loc || '').toUpperCase();
        const match = location.match(/^([A-Z]+)?(\d+)(.*)$/);
        if (match) return { prefix: match[1] || '', number: parseInt(match[2], 10), suffix: match[3] || '' };
        return { prefix: '', number: 0, suffix: location };
      };
      const locA = parseLocation(a.location || '');
      const locB = parseLocation(b.location || '');
      if (!locA.prefix && !locB.prefix) {
        if (locA.number !== locB.number) return locA.number - locB.number;
        return locA.suffix.localeCompare(locB.suffix);
      }
      if (!locA.prefix && locB.prefix) return -1;
      if (locA.prefix && !locB.prefix) return 1;
      if (locA.prefix !== locB.prefix) return locA.prefix.localeCompare(locB.prefix);
      if (locA.number !== locB.number) return locA.number - locB.number;
      return locA.suffix.localeCompare(locB.suffix);
    });
    
    // —— 防御性去重：即使上游 licenses 有重复，merged/filtered 列表也要确保 license_id 唯一 ——
    const beforeDedupeCount = result.length;
    result = dedupeLicensesById(result);
    if (result.length !== beforeDedupeCount) {
      console.warn(`%c[LicenseList] filtered/merged list defensive dedupe: ${beforeDedupeCount} → ${result.length} (removed ${beforeDedupeCount - result.length} duplicates by license_id)`, 'color:#b45309;font-weight:bold;');
    }

    // —— Debug: merged list (licenses merged + filtered + sorted + deduped, just before pagination) ——
    console.groupCollapsed('%c[LicenseList] merged list (after filter + sort + dedupe)', 'color:#7c3aed;font-weight:bold;');
    console.log('count:', result.length);
    console.log('ids:', result.map(l => l.id));
    console.groupEnd();

    setFilteredLicenses(result);
  }, [licenses, searchTerm, columnFilters, expirationDateFilter, renewalDateFilter]);

  // 分页计算
  const totalPages = Math.ceil(filteredLicenses.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedLicenses = filteredLicenses.slice(startIndex, endIndex);

  // 当筛选条件变化时重置页码
  useEffect(() => {
    if (restoringRef.current) return;
    setCurrentPage(1);
  }, [searchTerm, columnFilters, expirationDateFilter, renewalDateFilter, pageSize]);

  // —— Debug: final rendered list (paginatedLicenses — the actual rows rendered to the table) ——
  useEffect(() => {
    console.groupCollapsed('%c[LicenseList] final rendered list (paginatedLicenses → actual rows rendered)', 'color:#be123c;font-weight:bold;');
    console.log('page:', currentPage, 'pageSize:', pageSize, 'totalPages:', totalPages, 'total filtered:', filteredLicenses.length);
    console.log('count:', paginatedLicenses.length);
    console.log('ids:', paginatedLicenses.map(l => l.id));
    console.log('records:', paginatedLicenses);
    console.groupEnd();
    // 渲染分页数据去重检查
    const dup = findDuplicateIds(paginatedLicenses);
    if (dup.size > 0) {
      console.error('%c[LicenseList] ❌ DUPLICATES in final rendered paginatedLicenses! duplicate ids:', 'color:#be123c;font-weight:bold;', dup);
    }
  }, [paginatedLicenses, currentPage, pageSize, totalPages, filteredLicenses.length]);

  const fetchLicenses = async () => {
    setLoading(true);
    try {
      const response = await licenseApi.getAll();
      const raw = response.data;
      // —— Debug: raw API response ——
      const rawList = Array.isArray(raw) ? raw : (raw?.list || []);
      console.groupCollapsed('%c[LicenseList] raw API response (licenseApi.getAll())', 'color:#2563eb;font-weight:bold;');
      console.log('count:', rawList.length);
      console.log('ids:', rawList.map(l => l.id).slice(0, 100), rawList.length > 100 ? `... (first 100 shown, total ${rawList.length})` : '');
      console.groupEnd();

      const deduped = dedupeLicensesById(rawList);
      if (deduped.length !== rawList.length) {
        console.warn(`%c[LicenseList] fetchLicenses deduplicated: raw=${rawList.length} → unique=${deduped.length} (removed ${rawList.length - deduped.length} duplicates by license_id)`, 'color:#b45309;font-weight:bold;');
        const dupIds = findDuplicateIds(rawList);
        if (dupIds.size > 0) console.warn('[LicenseList] duplicate license_ids found:', dupIds);
      }
      setLicenses(deduped);
    } catch (error) {
      console.error('Failed to fetch licenses:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await companyApi.getAll();
      const raw = response.data;
      const list = Array.isArray(raw) ? raw : (raw?.list || []);
      setAllCompanies(list);
    } catch (error) {
      console.error('Failed to fetch companies:', error);
    }
  };

  // 获取已删除的牌照
  const fetchDeletedLicenses = async () => {
    try {
      const response = await licenseApi.getDeleted();
      const raw = response.data;
      const deduped = dedupeLicensesById(Array.isArray(raw) ? raw : []);
      if (deduped.length !== raw.length) {
        console.warn(`[LicenseList] fetchDeletedLicenses deduplicated: raw=${raw.length} → unique=${deduped.length}`);
      }
      setDeletedLicenses(deduped);
    } catch (error) {
      console.error('Failed to fetch deleted licenses:', error);
    }
  };

  const handleDelete = async (id: number) => {
    const deleted_reason = window.prompt('請輸入刪除原因：');
    if (deleted_reason !== null) {
      try {
        await licenseApi.delete(id, deleted_reason || undefined);
        // 从显示列表中移除
        setLicenses(licenses.filter(l => l.id !== id));
        setFilteredLicenses(filteredLicenses.filter(l => l.id !== id));
      } catch (error) {
        console.error('Delete failed:', error);
      }
    }
  };

  const handleRestore = async (id: number) => {
    if (window.confirm('Restore this license?')) {
      try {
        await licenseApi.restore(id);
        setDeletedLicenses(deletedLicenses.filter(l => l.id !== id));
        // 刷新正常列表
        fetchLicenses();
      } catch (error) {
        console.error('Restore failed:', error);
      }
    }
  };

  const handlePermanentDelete = async (id: number) => {
    if (window.confirm('Permanently delete this license? This cannot be undone!')) {
      try {
        await licenseApi.permanentDelete(id);
        setDeletedLicenses(deletedLicenses.filter(l => l.id !== id));
      } catch (error) {
        console.error('Permanent delete failed:', error);
      }
    }
  };

  const handleApplyExpense = (license: License) => {
    try {
      const statusNorm = (license.status || '').toString().trim();
      const applyStatuses = new Set(['Applying', '待Apply', 'Pending']);
      let expenseTypeLabel = 'Application';
      let expenseTypeCode = 'Applying';
      let expenseTypeCN = '申請';
      // —— 除明确的「申請中」类状态外，其他所有状态（含 Renewing / 待變更 / 待Cancel / Cancelling / HOLD / Cancelled / Active / Expired 等）一律按續期处理
      if (!applyStatuses.has(statusNorm)) {
        expenseTypeLabel = 'Renewal';
        expenseTypeCode = 'Renewal';
        expenseTypeCN = '續期';
      }

      const displayCompany = (license.company_name || '').trim();
      const head = [
        displayCompany,
        license.department?.trim(),
        license.certificate_name?.trim()
      ].filter(Boolean).join(' - ');
      const expensePurpose = head ? `${head} ${expenseTypeCN}` : expenseTypeCN;

      const params = new URLSearchParams();
      if (displayCompany) params.set('expense_company', displayCompany);
      if (expensePurpose) params.set('expense_purpose', expensePurpose);
      if (license.location?.trim()) params.set('warehouse', license.location.trim());
      if (license.department?.trim()) params.set('department', license.department.trim());
      if (license.certificate_name?.trim()) params.set('certificate_name', license.certificate_name.trim());
      params.set('quantity', '1');
      params.set('unit', '份');

      let cycleType = 'ONE_TIME';
      let cycleDisplay = 'ONE_TIME';
      let startDateStr = '';
      let endDateStr = '';

      if (expenseTypeCode === 'Renewal' && license.expiration_date) {
        try {
          const expiry = new Date(license.expiration_date);
          if (!isNaN(expiry.getTime())) {
            const startD = new Date(expiry.getFullYear(), expiry.getMonth(), expiry.getDate() + 1);
            const renewalType = (license.renewal_type || 'Annually').toString().trim().toLowerCase();
            const endD = new Date(startD);
            switch (renewalType) {
              case 'annually':
              case '1-year':
              case '1 year':
              case '1year':
                endD.setFullYear(endD.getFullYear() + 1);
                break;
              case 'quarterly':
                endD.setMonth(endD.getMonth() + 3);
                break;
              case 'semi-annually':
              case 'semi annually':
              case 'semiannually':
                endD.setMonth(endD.getMonth() + 6);
                break;
              case '2-year':
              case '2 year':
              case '2year':
                endD.setFullYear(endD.getFullYear() + 2);
                break;
              case '3-year':
              case '3 year':
              case '3year':
                endD.setFullYear(endD.getFullYear() + 3);
                break;
              case '4-year':
              case '4 year':
              case '4year':
                endD.setFullYear(endD.getFullYear() + 4);
                break;
              case '5-year':
              case '5 year':
              case '5year':
                endD.setFullYear(endD.getFullYear() + 5);
                break;
              case '10-year':
              case '10 year':
              case '10year':
                endD.setFullYear(endD.getFullYear() + 10);
                break;
              default:
                endD.setFullYear(endD.getFullYear() + 1);
            }
            const endDFinal = new Date(endD.getFullYear(), endD.getMonth(), endD.getDate() - 1);

            const fmt = (d: Date) => d.toISOString().split('T')[0];
            startDateStr = fmt(startD);
            endDateStr = fmt(endDFinal);
            cycleDisplay = `${startDateStr} ~ ${endDateStr}`;
            cycleType = 'PERIOD_BASED';
          }
        } catch (err) {
          console.warn('Cycle calculation failed, fallback to ONE_TIME:', err);
          cycleType = 'ONE_TIME';
          cycleDisplay = 'ONE_TIME';
        }
      }

      params.set('expense_cycle', cycleDisplay);
      params.set('cycle_type', cycleType);
      if (startDateStr) params.set('start_date', startDateStr);
      if (endDateStr) params.set('end_date', endDateStr);
      params.set('expense_type_label', expenseTypeLabel);

      params.set('source', 'license');
      params.set('license_id', String(license.id));

      saveLicenseState(); setLicenseFromPage(); navigate('/expense/new?' + params.toString(), { state: { from: location.pathname + location.search } });
    } catch (error) {
      console.error('Apply expense failed:', error);
      alert('Failed to open expense application. Please create manually');
    }
  };

  const handleImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImporting(true);
    try {
      console.log('Starting import for file:', file.name);
      const { sheets, data } = await parseExcelFile(file);
      console.log('Parsed sheets:', sheets);
      console.log('Parsed data keys:', Object.keys(data));
      
      if (sheets.length === 0) {
        alert('No worksheets found in the Excel file');
        return;
      }
      
      const firstSheet = sheets[0];
      console.log('First sheet:', firstSheet);
      const rawData = data[firstSheet];
      console.log('Raw data length:', rawData.length);
      console.log('First row keys:', Object.keys(rawData[0] || {}));
      
      if (rawData.length === 0) {
        alert('No data rows found in the worksheet');
        return;
      }
      
      const licenseDataList = convertToLicenseData(rawData);
      console.log('Converted license data length:', licenseDataList.length);
      console.log('Sample converted data:', licenseDataList[0]);
      
      if (licenseDataList.length === 0) {
        alert('No License data could be recognized. Please ensure the Excel file contains a "Company" or "Certificate Name" column.\n\nFirst 5 rows (keys preview):' + 
          rawData.slice(0, 5).map((row, i) => 
            `\nRow ${i + 1}: ${JSON.stringify(Object.keys(row).slice(0, 5))}`
          ).join(''));
        return;
      }
      
      // Deduplication (based on company_name + certificate_name + license_no)
      const uniqueLicenses = [];
      const seen = new Set();
      const invalidRecords: string[] = [];
      
      for (const licenseData of licenseDataList) {
        // Validate required fields
        if (!licenseData.company_name && !licenseData.certificate_name) {
          invalidRecords.push('Missing company name and certificate name');
          continue;
        }
        
        const key = `${licenseData.company_name || ''}|${licenseData.certificate_name || ''}|${licenseData.license_no || ''}`;
        if (!seen.has(key)) {
          seen.add(key);
          uniqueLicenses.push(licenseData);
        } else {
          console.log('Skipping duplicate license:', licenseData);
        }
      }
      
      if (invalidRecords.length > 0) {
        alert(`${invalidRecords.length} invalid record(s) skipped (missing company and certificate name)`);
      }
      
      if (uniqueLicenses.length < licenseDataList.length - invalidRecords.length) {
        const duplicateCount = licenseDataList.length - invalidRecords.length - uniqueLicenses.length;
        alert(`${duplicateCount} duplicate record(s) auto-skipped`);
      }
      
      if (uniqueLicenses.length === 0) {
        alert('No valid License data to import');
        return;
      }
      
      let successCount = 0;
      let failCount = 0;
      let errorMessages: string[] = [];
      
      for (const licenseData of uniqueLicenses) {
        try {
          await licenseApi.create(licenseData as Omit<License, 'id' | 'created_at' | 'updated_at'>);
          successCount++;
        } catch (error: any) {
          failCount++;
          // Collect the first 5 errors for summary
          if (errorMessages.length < 5) {
            const errorMsg = error.response?.data?.error || error.message || 'Unknown error';
            errorMessages.push(`${licenseData.company_name || 'Unknown'}: ${errorMsg}`);
            console.error('Import failed for license:', licenseData.company_name, errorMsg);
          }
        }
      }
      
      await fetchLicenses();
      
      if (failCount > 0) {
        let errorSummary = `Import completed: ${successCount} succeeded, ${failCount} failed\n\n`;
        if (errorMessages.length > 0) {
          errorSummary += `Sample errors:\n${errorMessages.join('\n')}`;
        }
        alert(errorSummary);
      } else {
        alert(`Successfully imported ${successCount} License record(s)!`);
      }
    } catch (error: any) {
      console.error('Import failed:', error);
      alert('Import failed: ' + (error.message || 'Please verify the file format is correct'));
    } finally {
      setImporting(false);
      // Clear input so the same file can be selected again
      event.target.value = '';
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await licenseApi.export();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `licenses_${new Date().toISOString().split('T')[0]}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  // —— Status 规范化：支持用户指定的 9 个合法状态值
  //   Active / Renewing / Applying / Cancelling / Cancelled / 待Cancel / 待變更 / HOLD / 待Apply
  //   其他旧值（Expired / Pending / Inactive / Suspended）按过期/待办语义匹配展示徽章
  //   空值 → 基于 Expiration Date 计算：d<0→Overdue | d≤90→Expiring | Active
  const getNormalizedStatus = (license: License): { label: string; bg: string; text: string } => {
    const raw = (license.status || '').trim();
    switch (raw) {
      case 'Active':       return { label: 'Active',       bg: 'bg-green-100',  text: 'text-green-800'  };
      case 'Renewing':     return { label: 'Renewing',     bg: 'bg-blue-100',   text: 'text-blue-800'   };
      case 'Applying':     return { label: 'Applying',     bg: 'bg-yellow-100', text: 'text-yellow-800' };
      case 'Cancelling':   return { label: 'Cancelling',   bg: 'bg-orange-100', text: 'text-orange-800' };
      case 'Cancelled':    return { label: 'Cancelled',    bg: 'bg-slate-200',  text: 'text-slate-700'  };
      case '待Cancel':     return { label: '待Cancel',     bg: 'bg-orange-100', text: 'text-orange-800' };
      case '待變更':       return { label: '待變更',       bg: 'bg-purple-100', text: 'text-purple-800' };
      case 'HOLD':         return { label: 'HOLD',         bg: 'bg-pink-100',   text: 'text-pink-800'   };
      case '待Apply':      return { label: '待Apply',      bg: 'bg-amber-100',  text: 'text-amber-800'  };
      case 'Deleted':      return { label: 'Deleted',      bg: 'bg-red-100',    text: 'text-red-800'    };
      case 'Expired':      return { label: 'Overdue',      bg: 'bg-red-100',    text: 'text-red-800'    };
      case 'Pending':      return { label: 'Applying',     bg: 'bg-yellow-100', text: 'text-yellow-800' };
      case 'Inactive':     return { label: 'Inactive',     bg: 'bg-gray-100',   text: 'text-gray-800'   };
      case 'Suspended':    return { label: 'Suspended',    bg: 'bg-gray-200',   text: 'text-gray-700'   };
    }
    if (raw) return { label: raw, bg: 'bg-gray-100', text: 'text-gray-800' };

    const d = getDaysRemaining(license.expiration_date);
    if (d < 0) return { label: 'Overdue',  bg: 'bg-red-100',    text: 'text-red-800'    };
    if (d <= 90) return { label: 'Expiring', bg: 'bg-yellow-100', text: 'text-yellow-800' };
    return { label: 'Active', bg: 'bg-green-100', text: 'text-green-800' };
  };
  const getStatusColor = (expirationDate: string) => {
    const daysRemaining = getDaysRemaining(expirationDate);
    if (daysRemaining < 0) return { bg: 'bg-red-100', text: 'text-red-800', label: 'Overdue' };
    if (daysRemaining <= 90) return { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Expiring' };
    return { bg: 'bg-green-100', text: 'text-green-800', label: 'Active' };
  };

  // 固定 License Status 选项（按用户规范的 9 个值），合并现有数据中存在的其他状态值（向后兼容）
  const statuses = useMemo(() => {
    const uniqueFromData = Array.from(new Set(licenses.map(l => l.status).filter(Boolean)));
    const mergedSet = new Set([...CANONICAL_LICENSE_STATUSES, ...uniqueFromData]);
    return Array.from(mergedSet);
  }, [licenses]);

  // Get all unique categories, companies and locations
  const categories = Array.from(new Set(licenses.map(l => l.category).filter(Boolean)));
  const companies = Array.from(new Set(licenses.map(l => l.company_name).filter(Boolean)));
  const locations = Array.from(new Set(licenses.map(l => l.location).filter(Boolean)));
  // ============================================================
  // Faceted 篩選資料：每個彈窗的選項與計數，皆以「套用其他所有篩選後」的資料集計算
  // → 一環扣一環：先選 Category=Business License，再開 Company 彈窗，
  //   SJ 旁邊的數字就是「SJ 的 Business License 數」，而非 SJ 全部牌照數
  // ============================================================
  const FACET_COLS: FacetCol[] = ['company_name', 'location', 'certificate_name', 'category', 'status'];
  const facetData = useMemo(() => {
    const out = {} as Record<FacetCol, { options: string[]; counts: Map<string, number> }>;
    for (const col of FACET_COLS) {
      const rows = applyLicenseFilters(licenses, {
        searchTerm,
        columnFilters,
        expirationDateFilter,
        renewalDateFilter,
        skipColumn: col,
        skipDefaultStatusExclusion: col === 'status',
      });
      const counts = new Map<string, number>();
      for (const l of rows) {
        if (col === 'category') {
          for (const c of (l.category || '').split(',').map(s => s.trim()).filter(Boolean)) {
            counts.set(c, (counts.get(c) || 0) + 1);
          }
        } else {
          const v = ((l[col] as string) || '').trim();
          if (v) counts.set(v, (counts.get(v) || 0) + 1);
        }
      }
      // 已勾選的選項即使目前 0 筆也要保留顯示（才看得到、能取消）
      const optionSet = new Set<string>(counts.keys());
      for (const sel of columnFilters[col]) {
        if (sel) optionSet.add(sel);
      }
      out[col] = { options: Array.from(optionSet).sort(), counts };
    }
    return out;
  }, [licenses, searchTerm, columnFilters, expirationDateFilter, renewalDateFilter]);

  const licenseReportStats = useMemo(() => {
    const total = filteredLicenses.length;
    const expired = filteredLicenses.filter(l => {
      const d = getDaysRemaining(l.expiration_date);
      return d < 0;
    }).length;
    const expiring30 = filteredLicenses.filter(l => {
      const d = getDaysRemaining(l.expiration_date);
      return d >= 0 && d <= 30;
    }).length;
    const expiring60 = filteredLicenses.filter(l => {
      const d = getDaysRemaining(l.expiration_date);
      return d > 30 && d <= 60;
    }).length;
    const valid = filteredLicenses.filter(l => {
      const d = getDaysRemaining(l.expiration_date);
      return d > 60;
    }).length;

    const statusMap = new Map<string, number>();
    for (const l of filteredLicenses) {
      const s = (l.status || '').trim() || '(Unknown)';
      statusMap.set(s, (statusMap.get(s) || 0) + 1);
    }
    const byStatus = Array.from(statusMap.entries()).map(([status, count]) => ({ status, count })).sort((a, b) => b.count - a.count);

    const categoryMap = new Map<string, number>();
    for (const l of filteredLicenses) {
      const c = (l.category || '').trim() || '(Unspecified)';
      categoryMap.set(c, (categoryMap.get(c) || 0) + 1);
    }
    const byCategory = Array.from(categoryMap.entries()).map(([category, count]) => ({ category, count })).sort((a, b) => b.count - a.count);

    const companyMap = new Map<string, number>();
    for (const l of filteredLicenses) {
      const c = (l.company_name || '').trim() || '(Unspecified)';
      companyMap.set(c, (companyMap.get(c) || 0) + 1);
    }
    const byCompany = Array.from(companyMap.entries()).map(([company_name, count]) => ({ company_name, count })).sort((a, b) => b.count - a.count);

    return { total, expired, expiring30, expiring60, valid, byStatus, byCategory, byCompany };
  }, [filteredLicenses]);

  // === Group Analysis: Group → Companies (by nickname) → Licenses ===
  interface GroupAnalysisCompany {
    nickname: string;
    company_name: string;
    states: string[];
    licenses: License[];
    licenseCount: number;
  }

  interface GroupAnalysisItem {
    groupName: string;
    companies: GroupAnalysisCompany[];
    licenseCount: number;
    companyCount: number;
  }

  const groupAnalysis = useMemo(() => {
    if (allCompanies.length === 0 && licenses.length === 0) return [];

    // Build company_id → Company map
    const companyById = new Map<number, Company>();
    for (const c of allCompanies) {
      if (c.id) companyById.set(c.id, c);
    }

    // Debug: check group_name availability
    const companiesWithoutGroup = allCompanies.filter(c => !c.group_name || c.group_name.trim() === '');
    if (companiesWithoutGroup.length > 0) {
      console.warn('[GroupAnalysis] companies without group_name:', companiesWithoutGroup.map(c => ({
        id: c.id, name: c.company_name, nickname: c.nickname, group_name: c.group_name
      })));
    }
    console.log('[GroupAnalysis] allCompanies count:', allCompanies.length,
      'with group_name:', allCompanies.filter(c => c.group_name && c.group_name.trim()).length);

    // Build normalized nickname → Company[] map (for grouping same nickname)
    const nicknameMap = new Map<string, Company[]>();
    // Also build base nickname map: "HKHI（HK1）" → "HKHI"
    const baseNicknameMap = new Map<string, string>(); // base → full
    for (const c of allCompanies) {
      const key = (c.nickname || '').trim().toUpperCase();
      if (!key) continue;
      if (!nicknameMap.has(key)) nicknameMap.set(key, []);
      nicknameMap.get(key)!.push(c);

      // Strip trailing parenthetical suffix like （HK1） or (HK1)
      const baseKey = key.replace(/[（(][^（()）]*[)）]\s*$/, '').trim();
      if (baseKey && baseKey !== key) {
        if (!baseNicknameMap.has(baseKey)) baseNicknameMap.set(baseKey, key);
      }
    }

    // Match license company_name to company nickname (fallback for licenses without company_id)
    const matchNickname = (name: string): string | null => {
      const upper = name.trim().toUpperCase();
      if (!upper) return null;
      // Direct match
      if (nicknameMap.has(upper)) return upper;

      // Word-based matching first: split by separators, check each word and combinations
      // This ensures "88 TRUCKING EX LLC" matches "EX" not "88"
      const words = upper.split(/[\s,./&()（）-]+/).filter(Boolean);
      // Try multi-word combinations first (e.g., "SJ 88" could be two words)
      for (let len = words.length; len >= 1; len--) {
        for (let i = 0; i <= words.length - len; i++) {
          const combined = words.slice(i, i + len).join(' ');
          if (nicknameMap.has(combined)) return combined;
        }
      }

      // Word boundary match: check if nickname appears as a whole word
      // (longest nicknames first to avoid partial matching)
      const sortedNicks = Array.from(nicknameMap.keys()).sort((a, b) => b.length - a.length);
      for (const nick of sortedNicks) {
        const escaped = nick.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const regex = new RegExp(`(^|[\\s,&()（）/-])${escaped}([\\s,&()（）/-]|$)`);
        if (regex.test(upper)) return nick;
      }

      // Number extraction (SJ88 → 88, 88SJ → 88)
      const numExtract = upper.replace(/[^0-9]/g, '');
      if (numExtract && nicknameMap.has(numExtract)) return numExtract;

      // Letter extraction (SJ88 → SJ)
      const letterExtract = upper.replace(/[^A-Za-z]/g, '');
      if (letterExtract && nicknameMap.has(letterExtract)) return letterExtract;

      // Trailing number (SJ88 → 88)
      const trailMatch = upper.match(/^([A-Za-z]+)(\d+)$/);
      if (trailMatch) {
        const numPart = trailMatch[2];
        if (nicknameMap.has(numPart)) return numPart;
      }
      // Leading number (88SJ → 88)
      const leadMatch = upper.match(/^(\d+)([A-Za-z]+)$/);
      if (leadMatch) {
        const numPart = leadMatch[1];
        if (nicknameMap.has(numPart)) return numPart;
      }

      // First word fallback
      const firstWord = words[0];
      if (firstWord && nicknameMap.has(firstWord)) return firstWord;

      // Base nickname match: "HKHI" → "HKHI（HK1）"
      if (baseNicknameMap.has(upper)) return baseNicknameMap.get(upper)!;

      // Base match with suffix stripping on input too
      const inputBase = upper.replace(/[（(][^（()）]*[)）]\s*$/, '').trim();
      if (inputBase && baseNicknameMap.has(inputBase)) return baseNicknameMap.get(inputBase)!;

      // Word-based base matching
      for (let len = words.length; len >= 1; len--) {
        for (let i = 0; i <= words.length - len; i++) {
          const combined = words.slice(i, i + len).join(' ');
          if (baseNicknameMap.has(combined)) return baseNicknameMap.get(combined)!;
        }
      }

      return null;
    };

    // Step 1: Group licenses by company_id (direct mapping)
    const companyIdLicensesMap = new Map<number, License[]>();
    const unmatchedLicenses: License[] = [];
    let matchedById = 0;

    for (const lic of filteredLicenses) {
      if (lic.company_id && companyById.has(lic.company_id)) {
        if (!companyIdLicensesMap.has(lic.company_id)) companyIdLicensesMap.set(lic.company_id, []);
        companyIdLicensesMap.get(lic.company_id)!.push(lic);
        matchedById++;
      } else {
        unmatchedLicenses.push(lic);
      }
    }

    // Debug: log matching stats
    console.log('[GroupAnalysis] total licenses:', filteredLicenses.length,
      'matched by company_id:', matchedById,
      'unmatched:', unmatchedLicenses.length);
    if (unmatchedLicenses.length > 0 && unmatchedLicenses.length <= 10) {
      console.log('[GroupAnalysis] unmatched sample:', unmatchedLicenses.map(l => ({
        id: l.id, company_id: l.company_id, company_name: l.company_name, cert: l.certificate_name
      })));
    }

    // Step 2: Group unmatched licenses by nickname matching
    const nicknameLicensesMap = new Map<string, License[]>();
    const stillUnmatched: License[] = [];
    for (const lic of unmatchedLicenses) {
      const nick = matchNickname(lic.company_name || '');
      if (nick) {
        if (!nicknameLicensesMap.has(nick)) nicknameLicensesMap.set(nick, []);
        nicknameLicensesMap.get(nick)!.push(lic);
      } else {
        stillUnmatched.push(lic);
      }
    }

    // Build group_name → GroupAnalysisItem
    const groupMap = new Map<string, GroupAnalysisItem>();

    // Process company_id matched licenses (group by nickname → group)
    // First, group all companies by nickname
    const nicknameToCompanies = new Map<string, Company[]>();
    for (const c of allCompanies) {
      const nick = (c.nickname || '').trim().toUpperCase();
      if (!nick) continue;
      if (!nicknameToCompanies.has(nick)) nicknameToCompanies.set(nick, []);
      nicknameToCompanies.get(nick)!.push(c);
    }

    // Aggregate licenses per nickname (company_id → company → nickname)
    const nicknameLicenseAgg = new Map<string, License[]>();
    for (const [companyId, licList] of companyIdLicensesMap.entries()) {
      const company = companyById.get(companyId);
      if (!company) continue;
      const nick = (company.nickname || '').trim().toUpperCase();
      if (!nick) {
        // Company has no nickname, try to match via company_name
        const fallbackNick = matchNickname(company.company_name || '');
        if (fallbackNick) {
          if (!nicknameLicenseAgg.has(fallbackNick)) nicknameLicenseAgg.set(fallbackNick, []);
          nicknameLicenseAgg.get(fallbackNick)!.push(...licList);
        } else {
          // Can't group, put in stillUnmatched
          stillUnmatched.push(...licList);
        }
      } else {
        if (!nicknameLicenseAgg.has(nick)) nicknameLicenseAgg.set(nick, []);
        nicknameLicenseAgg.get(nick)!.push(...licList);
      }
    }

    // Also add nickname-matched licenses
    for (const [nick, licList] of nicknameLicensesMap.entries()) {
      if (!nicknameLicenseAgg.has(nick)) nicknameLicenseAgg.set(nick, []);
      nicknameLicenseAgg.get(nick)!.push(...licList);
    }

    // Build groups from nickname aggregation
    for (const [nick, licList] of nicknameLicenseAgg.entries()) {
      const companies = nicknameToCompanies.get(nick) || [];
      // Prefer a company with valid group_name when multiple share the same nickname
      const companyWithGroup = companies.find(c => c.group_name && c.group_name.trim());
      const groupName = (companyWithGroup?.group_name || companies[0]?.group_name || '').trim() || '(No Group)';

      if (!groupMap.has(groupName)) {
        groupMap.set(groupName, {
          groupName,
          companies: [],
          licenseCount: 0,
          companyCount: 0,
        });
      }

      const group = groupMap.get(groupName)!;
      const states = [...new Set(companies.map(c => c.state).filter(Boolean))];
      const displayName = companyWithGroup?.company_name || companies[0]?.company_name || nick;

      group.companies.push({
        nickname: nick,
        company_name: displayName,
        states,
        licenses: licList,
        licenseCount: licList.length,
      });
    }

    // Sort: normal groups alphabetically, then (No Group) second-to-last, (未匹配) last
    const result = Array.from(groupMap.values()).sort((a, b) => {
      const specialOrder = (name: string): number => {
        if (name === '(未匹配)') return 3;
        if (name === '(No Group)') return 2;
        return 1;
      };
      const sa = specialOrder(a.groupName);
      const sb = specialOrder(b.groupName);
      if (sa !== sb) return sa - sb;
      return a.groupName.localeCompare(b.groupName);
    });

    // Add still-unmatched licenses
    if (stillUnmatched.length > 0) {
      const unmatchedGroup: GroupAnalysisItem = {
        groupName: '(未匹配)',
        companies: [],
        licenseCount: 0,
        companyCount: 0,
      };
      const unmatchCompanyMap = new Map<string, License[]>();
      for (const lic of stillUnmatched) {
        const name = (lic.company_name || '(未知)').trim();
        if (!unmatchCompanyMap.has(name)) unmatchCompanyMap.set(name, []);
        unmatchCompanyMap.get(name)!.push(lic);
      }
      for (const [name, licList] of unmatchCompanyMap.entries()) {
        unmatchedGroup.companies.push({
          nickname: name.toUpperCase(),
          company_name: name,
          states: [],
          licenses: licList,
          licenseCount: licList.length,
        });
      }
      unmatchedGroup.companies.sort((a, b) => b.licenseCount - a.licenseCount);
      unmatchedGroup.licenseCount = unmatchedGroup.companies.reduce((sum, c) => sum + c.licenseCount, 0);
      unmatchedGroup.companyCount = unmatchedGroup.companies.length;
      result.push(unmatchedGroup);
    }

    for (const g of result) {
      g.companies.sort((a, b) => b.licenseCount - a.licenseCount);
      g.licenseCount = g.companies.reduce((sum, c) => sum + c.licenseCount, 0);
      g.companyCount = g.companies.length;
    }

    // Debug: final group analysis
    console.log('[GroupAnalysis] final groups:', result.map(g => ({
      group: g.groupName,
      companies: g.companyCount,
      licenses: g.licenseCount,
      companyNames: g.companies.map(c => `${c.nickname}(${c.company_name})`)
    })));

    return result;
  }, [filteredLicenses, allCompanies]);

  // === Group Analysis filter data (with cascading options) ===
  const gaAllGroups = useMemo(
    () => Array.from(new Set(groupAnalysis.map(g => g.groupName))).sort((a, b) => {
      const sa = a === '(未匹配)' ? 3 : a === '(No Group)' ? 2 : 1;
      const sb = b === '(未匹配)' ? 3 : b === '(No Group)' ? 2 : 1;
      if (sa !== sb) return sa - sb;
      return a.localeCompare(b);
    }),
    [groupAnalysis]
  );

  // Companies filtered by selected groups
  const gaAvailableCompanies = useMemo(() => {
    const source = gaFilters.groups.length > 0
      ? groupAnalysis.filter(g => gaFilters.groups.includes(g.groupName))
      : groupAnalysis;
    return Array.from(new Set(source.flatMap(g => g.companies.map(c => c.nickname)))).sort();
  }, [groupAnalysis, gaFilters.groups]);

  // States filtered by selected groups + companies
  const gaAvailableStates = useMemo(() => {
    let source = groupAnalysis;
    if (gaFilters.groups.length > 0) source = source.filter(g => gaFilters.groups.includes(g.groupName));
    if (gaFilters.companies.length > 0) {
      source = source.map(g => ({
        ...g,
        companies: g.companies.filter(c => gaFilters.companies.includes(c.nickname))
      })).filter(g => g.companies.length > 0);
    }
    return Array.from(new Set(source.flatMap(g => g.companies.flatMap(c => c.states)))).sort();
  }, [groupAnalysis, gaFilters.groups, gaFilters.companies]);

  // Categories filtered by selected groups + companies + states
  const gaAvailableCategories = useMemo(() => {
    let source = groupAnalysis;
    if (gaFilters.groups.length > 0) source = source.filter(g => gaFilters.groups.includes(g.groupName));
    if (gaFilters.companies.length > 0) {
      source = source.map(g => ({
        ...g,
        companies: g.companies.filter(c => gaFilters.companies.includes(c.nickname))
      })).filter(g => g.companies.length > 0);
    }
    if (gaFilters.states.length > 0) {
      source = source.map(g => ({
        ...g,
        companies: g.companies.map(c => ({
          ...c,
          states: c.states.filter(s => gaFilters.states.includes(s)),
          licenses: c.licenses.filter(l => !l.location || gaFilters.states.some(st => l.location?.includes(st) || l.state === st))
        })).filter(c => c.licenses.length > 0)
      })).filter(g => g.companies.length > 0);
    }
    return Array.from(new Set(source.flatMap(g => g.companies.flatMap(c => c.licenses.flatMap(l => (l.category || '').split(',').map(s => s.trim()).filter(Boolean)))))).sort();
  }, [groupAnalysis, gaFilters.groups, gaFilters.companies, gaFilters.states]);

  // Auto-clean invalid downstream filter selections
  useEffect(() => {
    setGaFilters(f => {
      let changed = false;
      const next = { ...f };
      const availCompanies = gaAvailableCompanies;
      const badCompanies = f.companies.filter(c => !availCompanies.includes(c));
      if (badCompanies.length > 0) { next.companies = f.companies.filter(c => availCompanies.includes(c)); changed = true; }
      const availStates = gaAvailableStates;
      const badStates = f.states.filter(s => !availStates.includes(s));
      if (badStates.length > 0) { next.states = f.states.filter(s => availStates.includes(s)); changed = true; }
      const availCats = gaAvailableCategories;
      const badCats = f.categories.filter(c => !availCats.includes(c));
      if (badCats.length > 0) { next.categories = f.categories.filter(c => availCats.includes(c)); changed = true; }
      return changed ? next : f;
    });
  }, [gaAvailableCompanies, gaAvailableStates, gaAvailableCategories]);

  // === Filtered Group Analysis ===
  const filteredGroupAnalysis = useMemo(() => {
    const hasAnyFilter = gaFilters.groups.length > 0 || gaFilters.companies.length > 0 || gaFilters.states.length > 0 || gaFilters.categories.length > 0;
    if (!hasAnyFilter) return groupAnalysis;

    return groupAnalysis
      .filter(g => gaFilters.groups.length === 0 || gaFilters.groups.includes(g.groupName))
      .map(g => ({
        ...g,
        companies: g.companies
          .filter(c => gaFilters.companies.length === 0 || gaFilters.companies.includes(c.nickname))
          .map(c => ({
            ...c,
            states: gaFilters.states.length === 0 ? c.states : c.states.filter(s => gaFilters.states.includes(s)),
            licenses: gaFilters.categories.length === 0 ? c.licenses : c.licenses.filter(l => {
              const cats = (l.category || '').split(',').map(s => s.trim()).filter(Boolean);
              return cats.some(c => gaFilters.categories.includes(c));
            }),
            licenseCount: gaFilters.categories.length === 0 ? c.licenseCount : c.licenses.filter(l => {
              const cats = (l.category || '').split(',').map(s => s.trim()).filter(Boolean);
              return cats.some(c => gaFilters.categories.includes(c));
            }).length,
          }))
          .filter(c => c.licenseCount > 0),
      }))
      .filter(g => g.companies.length > 0)
      .map(g => ({
        ...g,
        licenseCount: g.companies.reduce((sum, c) => sum + c.licenseCount, 0),
        companyCount: g.companies.length,
      }));
  }, [groupAnalysis, gaFilters]);

  // === Renewal Reminder data ===
  const renewalData = useMemo(() => {
    const todayMs = Date.UTC(
      Number(moment.utc().format('YYYY')),
      Number(moment.utc().format('MM')) - 1,
      Number(moment.utc().format('DD'))
    );

    const withDays = filteredLicenses.map(l => ({
      license: l,
      days: getDaysRemaining(l.expiration_date),
    }));

    const expired = withDays
      .filter(x => x.days < 0)
      .sort((a, b) => a.days - b.days);

    const expiringSoon = withDays
      .filter(x => x.days >= 0 && x.days <= renewalDaysFilter)
      .sort((a, b) => a.days - b.days);

    return { expired, expiringSoon };
  }, [filteredLicenses, renewalDaysFilter]);

  return (
    <div className="fade-in">
      <div className="flex items-center justify-between mb-6">
        {showTrash ? (
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowTrash(false)}
              className="flex items-center space-x-2 px-3 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to List</span>
            </button>
            <h1 className="text-2xl font-bold text-orange-600">Trash</h1>
          </div>
        ) : (
          <div>
            <h1 className="text-2xl font-bold text-slate-800">{t('nav.licenseManagement')}</h1>
            <p className="text-slate-500 mt-1">{t('license.manageDesc')}</p>
          </div>
        )}
        <div className="flex items-center space-x-3">
          <button
            onClick={() => { saveLicenseState(); setLicenseFromPage(); navigate('/license/new', { state: { from: location.pathname + location.search } }); }}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>{t('license.addNew')}</span>
          </button>
          <button
            onClick={handleExport}
            disabled={exporting}
            className="flex items-center space-x-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50"
          >
            <Download className="w-4 h-4" />
            <span>{exporting ? t('msg.loading') : t('action.export')}</span>
          </button>
          <label className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg cursor-pointer">
              <Upload className="w-4 h-4" />
              <span>{importing ? t('msg.loading') : t('action.import')}</span>
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleImport}
                className="hidden"
              />
            </label>
            <button
              onClick={() => {
                setShowTrash(true);
                fetchDeletedLicenses();
              }}
              className="flex items-center space-x-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
            >
              <Trash className="w-4 h-4" />
              <span>Trash</span>
            </button>
        </div>
      </div>

      <ViewToggle
        viewMode={viewMode}
        onChange={setViewMode}
        show={!showTrash}
        variant="3tab"
      />

      {/* === RENEWAL REMINDER VIEW === */}
      {!showTrash && viewMode === 'renewal' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-800">Renewal Reminder</h2>
              <div className="flex items-center gap-2">
                {[30, 60, 90].map((days) => (
                  <button
                    key={days}
                    onClick={() => setRenewalDaysFilter(days)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      renewalDaysFilter === days
                        ? 'bg-orange-500 text-white shadow-sm'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {days} Days
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <div className="text-xs text-red-600 uppercase tracking-wider">Expired</div>
              <div className="text-3xl font-bold text-red-800 mt-1">{renewalData.expired.length}</div>
              <div className="text-xs text-red-500 mt-1">Licenses past expiration date</div>
            </div>
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
              <div className="text-xs text-orange-600 uppercase tracking-wider">Expiring in {renewalDaysFilter} Days</div>
              <div className="text-3xl font-bold text-orange-800 mt-1">{renewalData.expiringSoon.length}</div>
              <div className="text-xs text-orange-500 mt-1">Licenses approaching expiration</div>
            </div>
          </div>

          {renewalData.expired.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200">
              <div className="p-4 border-b border-slate-200 bg-red-50">
                <h2 className="text-lg font-bold text-red-800">🔴 Expired Licenses</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left px-4 py-2 text-xs font-semibold text-slate-500 uppercase">Company</th>
                      <th className="text-left px-4 py-2 text-xs font-semibold text-slate-500 uppercase">Location</th>
                      <th className="text-left px-4 py-2 text-xs font-semibold text-slate-500 uppercase">Certificate Name</th>
                      <th className="text-left px-4 py-2 text-xs font-semibold text-slate-500 uppercase">License #</th>
                      <th className="text-left px-4 py-2 text-xs font-semibold text-slate-500 uppercase">Expiration</th>
                      <th className="text-center px-4 py-2 text-xs font-semibold text-slate-500 uppercase">Days Overdue</th>
                      <th className="text-left px-4 py-2 text-xs font-semibold text-slate-500 uppercase">Status</th>
                      <th className="text-center px-4 py-2 text-xs font-semibold text-slate-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {renewalData.expired.slice(0, 25).map(({ license, days }) => (
                      <tr key={license.id} className="hover:bg-slate-50">
                        <td className="px-4 py-2 font-medium text-slate-800">{license.company_name || '-'}</td>
                        <td className="px-4 py-2 text-slate-700">{license.location || '-'}</td>
                        <td className="px-4 py-2 text-slate-700">{license.certificate_name || '-'}</td>
                        <td className="px-4 py-2 text-slate-700">{license.license_no || '-'}</td>
                        <td className="px-4 py-2 text-slate-700">
                          {license.expiration_date && license.expiration_date.trim() !== ''
                            ? moment.utc(license.expiration_date).format('YYYY-MM-DD') : '-'}
                        </td>
                        <td className="px-4 py-2 text-center">
                          <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-800">
                            {Math.abs(days)}d overdue
                          </span>
                        </td>
                        <td className="px-4 py-2 text-slate-700">{license.status || '-'}</td>
                        <td className="px-4 py-2 text-center">
                          <div className="flex items-center justify-center space-x-0.5">
                            <button
                              onClick={() => handleApplyExpense(license)}
                              className="p-1 text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 rounded transition-colors"
                              title="Apply Expense"
                            >
                              <Receipt className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => { saveLicenseState(); setLicenseFromPage(); navigate(`/license/edit/${license.id}`, { state: { from: location.pathname + location.search } }); }}
                              className="p-1 text-slate-500 hover:text-green-600 hover:bg-green-50 rounded transition-colors"
                              title="Edit"
                            >
                              <Edit2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {renewalData.expired.length > 25 && (
                  <div className="px-4 py-2 text-center text-sm text-slate-500 border-t border-slate-100">
                    Showing 25 of {renewalData.expired.length} expired licenses
                  </div>
                )}
              </div>
            </div>
          )}

          {renewalData.expiringSoon.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200">
              <div className="p-4 border-b border-slate-200 bg-orange-50">
                <h2 className="text-lg font-bold text-orange-800">🟡 Expiring Soon ({renewalDaysFilter} Days)</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left px-4 py-2 text-xs font-semibold text-slate-500 uppercase">Company</th>
                      <th className="text-left px-4 py-2 text-xs font-semibold text-slate-500 uppercase">Location</th>
                      <th className="text-left px-4 py-2 text-xs font-semibold text-slate-500 uppercase">Certificate Name</th>
                      <th className="text-left px-4 py-2 text-xs font-semibold text-slate-500 uppercase">License #</th>
                      <th className="text-left px-4 py-2 text-xs font-semibold text-slate-500 uppercase">Expiration</th>
                      <th className="text-center px-4 py-2 text-xs font-semibold text-slate-500 uppercase">Days Left</th>
                      <th className="text-left px-4 py-2 text-xs font-semibold text-slate-500 uppercase">Status</th>
                      <th className="text-center px-4 py-2 text-xs font-semibold text-slate-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {renewalData.expiringSoon.slice(0, 25).map(({ license, days }) => (
                      <tr key={license.id} className="hover:bg-slate-50">
                        <td className="px-4 py-2 font-medium text-slate-800">{license.company_name || '-'}</td>
                        <td className="px-4 py-2 text-slate-700">{license.location || '-'}</td>
                        <td className="px-4 py-2 text-slate-700">{license.certificate_name || '-'}</td>
                        <td className="px-4 py-2 text-slate-700">{license.license_no || '-'}</td>
                        <td className="px-4 py-2 text-slate-700">
                          {license.expiration_date && license.expiration_date.trim() !== ''
                            ? moment.utc(license.expiration_date).format('YYYY-MM-DD') : '-'}
                        </td>
                        <td className="px-4 py-2 text-center">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                            days <= 7 ? 'bg-red-100 text-red-800' : days <= 15 ? 'bg-orange-100 text-orange-800' : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {days}d
                          </span>
                        </td>
                        <td className="px-4 py-2 text-slate-700">{license.status || '-'}</td>
                        <td className="px-4 py-2 text-center">
                          <div className="flex items-center justify-center space-x-0.5">
                            <button
                              onClick={() => handleApplyExpense(license)}
                              className="p-1 text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 rounded transition-colors"
                              title="Apply Expense"
                            >
                              <Receipt className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => { saveLicenseState(); setLicenseFromPage(); navigate(`/license/edit/${license.id}`, { state: { from: location.pathname + location.search } }); }}
                              className="p-1 text-slate-500 hover:text-green-600 hover:bg-green-50 rounded transition-colors"
                              title="Edit"
                            >
                              <Edit2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {renewalData.expiringSoon.length > 25 && (
                  <div className="px-4 py-2 text-center text-sm text-slate-500 border-t border-slate-100">
                    Showing 25 of {renewalData.expiringSoon.length} expiring licenses
                  </div>
                )}
              </div>
            </div>
          )}

          {renewalData.expired.length === 0 && renewalData.expiringSoon.length === 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center">
              <div className="text-5xl mb-4">✅</div>
              <h3 className="text-lg font-semibold text-slate-800">All licenses are up to date</h3>
              <p className="text-sm text-slate-500 mt-2">No licenses expired or expiring within 30 days</p>
            </div>
          )}
        </div>
      )}

      {/* === LIST VIEW / REPORT VIEW === */}
      {(viewMode === 'list' || viewMode === 'report') && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200">
          <div className="p-4 border-b border-slate-200">
          <div className="flex flex-wrap items-center gap-4">
            <div className="relative flex-1 min-w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t('license.searchPlaceholder')}
                className="pl-10 pr-4 py-2 w-full bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* 回收站表格 */}
        {showTrash && (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead className="bg-orange-50">
                <tr>
                  <th className="text-center px-3 py-3 text-xs font-semibold text-orange-600 uppercase tracking-wider border-b border-orange-200">Company</th>
                  <th className="text-center px-3 py-3 text-xs font-semibold text-orange-600 uppercase tracking-wider border-b border-orange-200">Location</th>
                  <th className="text-center px-3 py-3 text-xs font-semibold text-orange-600 uppercase tracking-wider border-b border-orange-200">Certificate Name</th>
                  <th className="text-center px-3 py-3 text-xs font-semibold text-orange-600 uppercase tracking-wider border-b border-orange-200">Deleted Date</th>
                  <th className="text-center px-3 py-3 text-xs font-semibold text-orange-600 uppercase tracking-wider border-b border-orange-200">Reason</th>
                  <th className="text-center px-3 py-3 text-xs font-semibold text-orange-600 uppercase tracking-wider border-b border-orange-200">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-orange-100">
                {deletedLicenses.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center text-slate-500">
                      Trash is empty
                    </td>
                  </tr>
                ) : (
                  deletedLicenses.map((license) => (
                    <tr key={license.id} className="hover:bg-orange-50">
                      <td className="px-3 py-3 text-sm text-slate-700 text-center">{license.company_name}</td>
                      <td className="px-3 py-3 text-sm text-slate-700 text-center">{license.location}</td>
                      <td className="px-3 py-3 text-sm text-slate-700 text-center">{license.certificate_name}</td>
                      <td className="px-3 py-3 text-sm text-slate-700 text-center">
                        {license.deleted_at ? new Date(license.deleted_at).toLocaleDateString() : '-'}
                      </td>
                      <td className="px-3 py-3 text-sm text-slate-700 text-center">
                        {license.deleted_reason || '-'}
                      </td>
                      <td className="px-3 py-3 text-center">
                        <div className="flex items-center justify-center space-x-1.5">
                          <button
                            onClick={() => { saveLicenseState(); setLicenseFromPage(); navigate(`/license/edit/${license.id}`, { state: { from: location.pathname + location.search } }); }}
                            className="p-1.5 text-slate-500 hover:text-green-600 hover:bg-green-50 rounded transition-colors"
                            title="Edit"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleRestore(license.id)}
                            className="p-1.5 text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 rounded transition-colors"
                            title="Restore"
                          >
                            <RotateCcw className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handlePermanentDelete(license.id)}
                            className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                            title="Permanently Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {!showTrash && viewMode === 'list' && (
          <div className="overflow-visible">
            <table className="w-full border-collapse table-fixed">
              <colgroup>
                <col className="w-[14%]" />
                <col className="w-[7%]" />
                <col className="w-[14%]" />
                <col className="w-[8%]" />
                <col className="w-[10%]" />
                <col className="w-[10%]" />
                <col className="w-[10%]" />
                <col className="w-[9%]" />
                <col className="w-[8%]" />
              </colgroup>
            <thead className="bg-slate-50">
              <tr>
                <th className="text-center px-3 py-2.5 text-[11px] font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-200">
                  <div className="flex items-center justify-center gap-1 relative">
                    <span className="truncate">{t('license.companyName')}</span>
                    <button
                      onClick={() => setActiveFilterCol(activeFilterCol === 'company_name' ? null : 'company_name')}
                      className={`p-0.5 rounded transition-colors ${columnFilters.company_name.length > 0 ? 'text-blue-600' : 'text-slate-400 hover:text-slate-600'}`}
                      title={`Filter by ${t('license.companyName')}`}
                    >
                      <Filter className="w-3 h-3" />
                    </button>
                    {activeFilterCol === 'company_name' && (
                      <div ref={activeFilterCol === 'company_name' ? activeFilterColRef : undefined} className="absolute top-full left-0 mt-1 w-64 bg-white border border-slate-200 rounded-lg shadow-lg z-50 py-1">
                        <div className="px-2 pb-1">
                          <input
                            type="text"
                            placeholder="Search..."
                            value={filterSearch['company_name'] || ''}
                            onChange={(e) => setFilterSearch(fs => ({ ...fs, company_name: e.target.value }))}
                            onClick={(e) => e.stopPropagation()}
                            className="w-full px-2 py-1 text-xs border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-400 focus:border-blue-300"
                            autoFocus
                          />
                        </div>
                        <button
                          onClick={() => { setColumnFilters(f => ({ ...f, company_name: [] })); setFilterSearch(fs => ({ ...fs, company_name: '' })); setActiveFilterCol(null); }}
                          className="w-full flex items-center justify-between px-3 py-1 text-xs text-slate-500 hover:bg-slate-50 whitespace-nowrap"
                        >
                          <span>Clear</span>
                          {columnFilters.company_name.length > 0 && (<span className="text-slate-400 tabular-nums">{columnFilters.company_name.length} selected</span>)}
                        </button>
                        <div className="border-t border-slate-100 my-1"></div>
                        <div className="max-h-48 overflow-y-auto">
                          {(() => {
                            const filtered = facetData.company_name.options.filter(v => !filterSearch['company_name'] || v.toLowerCase().includes(filterSearch['company_name'].toLowerCase()));
                            if (filtered.length === 0) return <div className="px-3 py-2 text-xs text-slate-400">No matches</div>;
                            return filtered.map(v => {
                              const checked = columnFilters.company_name.includes(v);
                              const cnt = facetData.company_name.counts.get(v) ?? 0;
                              return (
                                <label key={v} className="flex items-start gap-2 px-3 py-1.5 text-xs cursor-pointer hover:bg-slate-50">
                                  <input type="checkbox" checked={checked} onChange={() => {
                                    setColumnFilters(f => ({ ...f, company_name: checked ? f.company_name.filter(x => x !== v) : [...f.company_name, v] }));
                                  }} className="w-3 h-3 mt-0.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500 flex-shrink-0" />
                                  <div className="flex-1 min-w-0 flex items-start justify-between gap-2">
                                    <span className={`min-w-0 text-left break-words ${checked ? 'text-blue-600 font-medium' : 'text-slate-700'}`}>{v}</span>
                                    <span className="text-slate-400 tabular-nums flex-shrink-0">({cnt})</span>
                                  </div>
                                </label>
                              );
                            });
                          })()}
                        </div>
                      </div>
                    )}
                  </div>
                </th>
                <th className="text-center px-3 py-2.5 text-[11px] font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-200">
                  <div className="flex items-center justify-center gap-1 relative">
                    <span className="truncate">{t('license.location')}</span>
                    <button
                      onClick={() => setActiveFilterCol(activeFilterCol === 'location' ? null : 'location')}
                      className={`p-0.5 rounded transition-colors ${columnFilters.location.length > 0 ? 'text-blue-600' : 'text-slate-400 hover:text-slate-600'}`}
                      title={`Filter by ${t('license.location')}`}
                    >
                      <Filter className="w-3 h-3" />
                    </button>
                    {activeFilterCol === 'location' && (
                      <div ref={activeFilterCol === 'location' ? activeFilterColRef : undefined} className="absolute top-full left-1/2 -translate-x-1/2 mt-1 w-64 bg-white border border-slate-200 rounded-lg shadow-lg z-50 py-1">
                        <div className="px-2 pb-1">
                          <input
                            type="text"
                            placeholder="Search..."
                            value={filterSearch['location'] || ''}
                            onChange={(e) => setFilterSearch(fs => ({ ...fs, location: e.target.value }))}
                            onClick={(e) => e.stopPropagation()}
                            className="w-full px-2 py-1 text-xs border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-400 focus:border-blue-300"
                            autoFocus
                          />
                        </div>
                        <button
                          onClick={() => { setColumnFilters(f => ({ ...f, location: [] })); setFilterSearch(fs => ({ ...fs, location: '' })); setActiveFilterCol(null); }}
                          className="w-full flex items-center justify-between px-3 py-1 text-xs text-slate-500 hover:bg-slate-50 whitespace-nowrap"
                        >
                          <span>Clear</span>
                          {columnFilters.location.length > 0 && (<span className="text-slate-400 tabular-nums">{columnFilters.location.length} selected</span>)}
                        </button>
                        <div className="border-t border-slate-100 my-1"></div>
                        <div className="max-h-48 overflow-y-auto">
                          {(() => {
                            const filtered = facetData.location.options.filter(v => !filterSearch['location'] || v.toLowerCase().includes(filterSearch['location'].toLowerCase()));
                            if (filtered.length === 0) return <div className="px-3 py-2 text-xs text-slate-400">No matches</div>;
                            return filtered.map(v => {
                              const checked = columnFilters.location.includes(v);
                              const cnt = facetData.location.counts.get(v) ?? 0;
                              return (
                                <label key={v} className="flex items-start gap-2 px-3 py-1.5 text-xs cursor-pointer hover:bg-slate-50">
                                  <input type="checkbox" checked={checked} onChange={() => {
                                    setColumnFilters(f => ({ ...f, location: checked ? f.location.filter(x => x !== v) : [...f.location, v] }));
                                  }} className="w-3 h-3 mt-0.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500 flex-shrink-0" />
                                  <div className="flex-1 min-w-0 flex items-start justify-between gap-2">
                                    <span className={`min-w-0 text-left break-words ${checked ? 'text-blue-600 font-medium' : 'text-slate-700'}`}>{v}</span>
                                    <span className="text-slate-400 tabular-nums flex-shrink-0">({cnt})</span>
                                  </div>
                                </label>
                              );
                            });
                          })()}
                        </div>
                      </div>
                    )}
                  </div>
                </th>
                <th className="text-center px-3 py-2.5 text-[11px] font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-200">
                  <div className="flex items-center justify-center gap-1 relative">
                    <span className="truncate">{t('license.certificateName')}</span>
                    <button
                      onClick={() => setActiveFilterCol(activeFilterCol === 'certificate_name' ? null : 'certificate_name')}
                      className={`p-0.5 rounded transition-colors ${columnFilters.certificate_name.length > 0 ? 'text-blue-600' : 'text-slate-400 hover:text-slate-600'}`}
                      title={`Filter by ${t('license.certificateName')}`}
                    >
                      <Filter className="w-3 h-3" />
                    </button>
                    {activeFilterCol === 'certificate_name' && (
                      <div ref={activeFilterCol === 'certificate_name' ? activeFilterColRef : undefined} className="absolute top-full left-1/2 -translate-x-1/2 mt-1 w-64 bg-white border border-slate-200 rounded-lg shadow-lg z-50 py-1">
                        <div className="px-2 pb-1">
                          <input
                            type="text"
                            placeholder="Search..."
                            value={filterSearch['certificate_name'] || ''}
                            onChange={(e) => setFilterSearch(fs => ({ ...fs, certificate_name: e.target.value }))}
                            onClick={(e) => e.stopPropagation()}
                            className="w-full px-2 py-1 text-xs border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-400 focus:border-blue-300"
                            autoFocus
                          />
                        </div>
                        <button
                          onClick={() => { setColumnFilters(f => ({ ...f, certificate_name: [] })); setFilterSearch(fs => ({ ...fs, certificate_name: '' })); setActiveFilterCol(null); }}
                          className="w-full flex items-center justify-between px-3 py-1 text-xs text-slate-500 hover:bg-slate-50 whitespace-nowrap"
                        >
                          <span>Clear</span>
                          {columnFilters.certificate_name.length > 0 && (<span className="text-slate-400 tabular-nums">{columnFilters.certificate_name.length} selected</span>)}
                        </button>
                        <div className="border-t border-slate-100 my-1"></div>
                        <div className="max-h-48 overflow-y-auto">
                          {(() => {
                            const filtered = facetData.certificate_name.options.filter(v => !filterSearch['certificate_name'] || v.toLowerCase().includes(filterSearch['certificate_name'].toLowerCase()));
                            if (filtered.length === 0) return <div className="px-3 py-2 text-xs text-slate-400">No matches</div>;
                            return filtered.map(v => {
                              const checked = columnFilters.certificate_name.includes(v);
                              const cnt = facetData.certificate_name.counts.get(v) ?? 0;
                              return (
                                <label key={v} className="flex items-start gap-2 px-3 py-1.5 text-xs cursor-pointer hover:bg-slate-50">
                                  <input type="checkbox" checked={checked} onChange={() => {
                                    setColumnFilters(f => ({ ...f, certificate_name: checked ? f.certificate_name.filter(x => x !== v) : [...f.certificate_name, v] }));
                                  }} className="w-3 h-3 mt-0.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500 flex-shrink-0" />
                                  <div className="flex-1 min-w-0 flex items-start justify-between gap-2">
                                    <span className={`min-w-0 text-left break-words ${checked ? 'text-blue-600 font-medium' : 'text-slate-700'}`}>{v}</span>
                                    <span className="text-slate-400 tabular-nums flex-shrink-0">({cnt})</span>
                                  </div>
                                </label>
                              );
                            });
                          })()}
                        </div>
                      </div>
                    )}
                  </div>
                </th>
                <th className="text-center px-3 py-2.5 text-[11px] font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-200">
                  <div className="flex items-center justify-center gap-1 relative">
                    <span className="truncate">Category</span>
                    <button
                      onClick={() => setActiveFilterCol(activeFilterCol === 'category' ? null : 'category')}
                      className={`p-0.5 rounded transition-colors ${columnFilters.category.length > 0 ? 'text-blue-600' : 'text-slate-400 hover:text-slate-600'}`}
                      title="Filter by Category"
                    >
                      <Filter className="w-3 h-3" />
                    </button>
                    {activeFilterCol === 'category' && (
                      <div ref={activeFilterCol === 'category' ? activeFilterColRef : undefined} className="absolute top-full left-1/2 -translate-x-1/2 mt-1 w-64 bg-white border border-slate-200 rounded-lg shadow-lg z-50 py-1">
                        <div className="px-2 pb-1">
                          <input
                            type="text"
                            placeholder="Search categories..."
                            value={filterSearch['category'] || ''}
                            onChange={(e) => setFilterSearch(fs => ({ ...fs, category: e.target.value }))}
                            onClick={(e) => e.stopPropagation()}
                            className="w-full px-2 py-1 text-xs border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-400 focus:border-blue-300"
                            autoFocus
                          />
                        </div>
                        <button
                          onClick={() => { setColumnFilters(f => ({ ...f, category: [] })); setFilterSearch(fs => ({ ...fs, category: '' })); setActiveFilterCol(null); }}
                          className="w-full flex items-center justify-between px-3 py-1 text-xs text-slate-500 hover:bg-slate-50 whitespace-nowrap"
                        >
                          <span>Clear</span>
                          {columnFilters.category.length > 0 && (<span className="text-slate-400 tabular-nums">{columnFilters.category.length} selected</span>)}
                        </button>
                        <div className="border-t border-slate-100 my-1"></div>
                        <div className="max-h-48 overflow-y-auto">
                          {(() => {
                            const filtered = facetData.category.options.filter(v => !filterSearch['category'] || v.toLowerCase().includes(filterSearch['category'].toLowerCase()));
                            if (filtered.length === 0) return <div className="px-3 py-2 text-xs text-slate-400">No matches</div>;
                            return filtered.map(v => {
                              const checked = columnFilters.category.includes(v);
                              const cnt = facetData.category.counts.get(v) ?? 0;
                              return (
                                <label key={v} className="flex items-start gap-2 px-3 py-1.5 text-xs cursor-pointer hover:bg-slate-50">
                                  <input type="checkbox" checked={checked} onChange={() => {
                                    setColumnFilters(f => ({ ...f, category: checked ? f.category.filter(x => x !== v) : [...f.category, v] }));
                                  }} className="w-3 h-3 mt-0.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500 flex-shrink-0" />
                                  <div className="flex-1 min-w-0 flex items-start justify-between gap-2">
                                    <span className={`min-w-0 text-left break-words ${checked ? 'text-blue-600 font-medium' : 'text-slate-700'}`}>{v}</span>
                                    <span className="text-slate-400 tabular-nums flex-shrink-0">({cnt})</span>
                                  </div>
                                </label>
                              );
                            });
                          })()}
                        </div>
                      </div>
                    )}
                  </div>
                </th>
                <th className="text-center px-3 py-2.5 text-[11px] font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-200">
                  <div className="flex items-center justify-center gap-1 relative">
                    <span className="truncate">{t('license.expirationDate')}</span>
                    <button
                      onClick={() => setActiveFilterCol(activeFilterCol === 'expiration_date' ? null : 'expiration_date')}
                      className={`p-0.5 rounded transition-colors ${isDateFilterActive(expirationDateFilter) ? 'text-blue-600' : 'text-slate-400 hover:text-slate-600'}`}
                      title={`Filter by ${t('license.expirationDate')}`}
                    >
                      <Filter className="w-3 h-3" />
                    </button>
                    {activeFilterCol === 'expiration_date' && (
                      <div ref={activeFilterCol === 'expiration_date' ? activeFilterColRef : undefined} className="absolute top-full left-1/2 -translate-x-1/2 mt-1 w-72 bg-white border border-slate-200 rounded-lg shadow-lg z-50 p-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="text-xs font-semibold text-slate-700">Filter Expiration Date</div>
                          <button
                            onClick={() => { setExpirationDateFilter(EMPTY_DATE_FILTER); setActiveFilterCol(null); }}
                            className="text-xs text-blue-600 hover:text-blue-800"
                          >
                            Clear
                          </button>
                        </div>
                        <select
                          value={expirationDateFilter.op}
                          onChange={e => setExpirationDateFilter({ ...expirationDateFilter, op: e.target.value as DateFilterOp })}
                          className="w-full px-2 py-1.5 text-xs border border-slate-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          {DATE_FILTER_OPS.map(o => (<option key={o.value} value={o.value}>{o.label}</option>))}
                        </select>
                        {expirationDateFilter.op && expirationDateFilter.op !== '' && (
                          <input
                            type="date"
                            value={expirationDateFilter.value}
                            onChange={e => setExpirationDateFilter({ ...expirationDateFilter, value: e.target.value })}
                            className="w-full px-2 py-1.5 text-xs border border-slate-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        )}
                        {isDateFilterActive(expirationDateFilter) && expirationDateFilter.value && (
                          <div className="text-xs text-slate-500 pt-1 border-t border-slate-100">
                            Active: {DATE_FILTER_OPS.find(o => o.value === expirationDateFilter.op)?.label} {expirationDateFilter.value}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </th>
                <th className="text-center px-3 py-2.5 text-[11px] font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-200">
                  <div className="flex items-center justify-center gap-1 relative">
                    <span className="truncate">{t('license.renewalDate')}</span>
                    <button
                      onClick={() => setActiveFilterCol(activeFilterCol === 'renewal_date' ? null : 'renewal_date')}
                      className={`p-0.5 rounded transition-colors ${isDateFilterActive(renewalDateFilter) ? 'text-blue-600' : 'text-slate-400 hover:text-slate-600'}`}
                      title={`Filter by ${t('license.renewalDate')}`}
                    >
                      <Filter className="w-3 h-3" />
                    </button>
                    {activeFilterCol === 'renewal_date' && (
                      <div ref={activeFilterCol === 'renewal_date' ? activeFilterColRef : undefined} className="absolute top-full left-1/2 -translate-x-1/2 mt-1 w-72 bg-white border border-slate-200 rounded-lg shadow-lg z-50 p-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="text-xs font-semibold text-slate-700">Filter Renewal Date</div>
                          <button
                            onClick={() => { setRenewalDateFilter(EMPTY_DATE_FILTER); setActiveFilterCol(null); }}
                            className="text-xs text-blue-600 hover:text-blue-800"
                          >
                            Clear
                          </button>
                        </div>
                        <select
                          value={renewalDateFilter.op}
                          onChange={e => setRenewalDateFilter({ ...renewalDateFilter, op: e.target.value as DateFilterOp })}
                          className="w-full px-2 py-1.5 text-xs border border-slate-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          {DATE_FILTER_OPS.map(o => (<option key={o.value} value={o.value}>{o.label}</option>))}
                        </select>
                        {renewalDateFilter.op && renewalDateFilter.op !== '' && (
                          <input
                            type="date"
                            value={renewalDateFilter.value}
                            onChange={e => setRenewalDateFilter({ ...renewalDateFilter, value: e.target.value })}
                            className="w-full px-2 py-1.5 text-xs border border-slate-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        )}
                        {isDateFilterActive(renewalDateFilter) && renewalDateFilter.value && (
                          <div className="text-xs text-slate-500 pt-1 border-t border-slate-100">
                            Active: {DATE_FILTER_OPS.find(o => o.value === renewalDateFilter.op)?.label} {renewalDateFilter.value}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </th>
                <th className="text-center px-3 py-2.5 text-[11px] font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-200">{t('license.daysRemaining')}</th>
                <th className="text-center px-3 py-2.5 text-[11px] font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-200">
                  <div className="flex items-center justify-center gap-1 relative">
                    <span className="truncate">{t('license.status')}</span>
                    <button
                      onClick={() => setActiveFilterCol(activeFilterCol === 'status' ? null : 'status')}
                      className={`p-0.5 rounded transition-colors ${columnFilters.status.length > 0 ? 'text-blue-600' : 'text-slate-400 hover:text-slate-600'}`}
                      title={`Filter by ${t('license.status')}`}
                    >
                      <Filter className="w-3 h-3" />
                    </button>
                    {activeFilterCol === 'status' && (
                      <div ref={activeFilterCol === 'status' ? activeFilterColRef : undefined} className="absolute top-full right-0 mt-1 w-64 bg-white border border-slate-200 rounded-lg shadow-lg z-50 py-1">
                        <div className="px-2 pb-1">
                          <input
                            type="text"
                            placeholder="Search..."
                            value={filterSearch['status'] || ''}
                            onChange={(e) => setFilterSearch(fs => ({ ...fs, status: e.target.value }))}
                            onClick={(e) => e.stopPropagation()}
                            className="w-full px-2 py-1 text-xs border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-400 focus:border-blue-300"
                            autoFocus
                          />
                        </div>
                        <button
                          onClick={() => { setColumnFilters(f => ({ ...f, status: [] })); setFilterSearch(fs => ({ ...fs, status: '' })); setActiveFilterCol(null); }}
                          className="w-full flex items-center justify-between px-3 py-1 text-xs text-slate-500 hover:bg-slate-50 whitespace-nowrap"
                        >
                          <span>Clear</span>
                          {columnFilters.status.length > 0 && (<span className="text-slate-400 tabular-nums">{columnFilters.status.length} selected</span>)}
                        </button>
                        <div className="border-t border-slate-100 my-1"></div>
                        <div className="max-h-48 overflow-y-auto">
                          {(() => {
                            const filtered = facetData.status.options.filter(v => !filterSearch['status'] || v.toLowerCase().includes(filterSearch['status'].toLowerCase()));
                            if (filtered.length === 0) return <div className="px-3 py-2 text-xs text-slate-400">No matches</div>;
                            return filtered.map(v => {
                              const checked = columnFilters.status.includes(v);
                              const cnt = facetData.status.counts.get(v) ?? 0;
                              return (
                                <label key={v} className="flex items-start gap-2 px-3 py-1.5 text-xs cursor-pointer hover:bg-slate-50">
                                  <input type="checkbox" checked={checked} onChange={() => {
                                    setColumnFilters(f => ({ ...f, status: checked ? f.status.filter(x => x !== v) : [...f.status, v] }));
                                  }} className="w-3 h-3 mt-0.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500 flex-shrink-0" />
                                  <div className="flex-1 min-w-0 flex items-start justify-between gap-2">
                                    <span className={`min-w-0 text-left break-words ${checked ? 'text-blue-600 font-medium' : 'text-slate-700'}`}>{v}</span>
                                    <span className="text-slate-400 tabular-nums flex-shrink-0">({cnt})</span>
                                  </div>
                                </label>
                              );
                            });
                          })()}
                        </div>
                      </div>
                    )}
                  </div>
                </th>
                <th className="text-center px-3 py-2.5 text-[11px] font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-200">{t('action.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-slate-500">
                    {t('msg.loading')}
                  </td>
                </tr>
              ) : filteredLicenses.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-slate-500">
                    {t('msg.noResults')}
                  </td>
                </tr>
              ) : (
                paginatedLicenses.map((license) => {
                  const normalized = getNormalizedStatus(license);
                  const days = getDaysRemaining(license.expiration_date);
                  const daysDisplay = (() => {
                    if (license.status === 'Cancelled') {
                      return <span className="text-slate-400">-</span>;
                    }
                    if (!license.expiration_date || license.expiration_date.trim() === '' ||
                        license.expiration_date === '0000-00-00' || license.expiration_date === '1900-01-01') {
                      return <span className="text-slate-400">-</span>;
                    }
                    if (days < 0) {
                      const n = Math.abs(days);
                      return <span className="font-semibold text-red-700">{n} day{n === 1 ? '' : 's'} overdue</span>;
                    }
                    return <span className="font-semibold text-slate-700">{days} day{days === 1 ? '' : 's'} left</span>;
                  })();
                  return (
                    <tr key={license.id} className="hover:bg-slate-50">
                      <td className="px-3 py-2.5 text-slate-800 font-medium text-center text-xs truncate" title={license.company_name}>
                        <div className="truncate">{license.company_name}</div>
                        {license.tags && (
                          <div className="mt-0.5 flex flex-wrap gap-0.5 justify-center">
                            {license.tags.split(',').filter(tag => tag.trim()).map((tag, idx) => (
                              <span key={idx} className="px-1 py-px bg-blue-100 text-blue-700 text-[10px] rounded-full">
                                {tag.trim()}
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-slate-600 text-xs text-center truncate" title={license.location || ''}>{license.location || '-'}</td>
                      <td className="px-3 py-2.5 text-slate-800 text-xs text-center truncate" title={license.certificate_name || ''}>{license.certificate_name || '-'}</td>
                      <td className="px-3 py-2.5 text-xs text-center text-slate-700">
                        {(license.category || '').split(',').map(s => s.trim()).filter(Boolean).length === 0 ? (
                          <span className="text-slate-400">-</span>
                        ) : (
                          <span>{(license.category || '').split(',').map(s => s.trim()).filter(Boolean).join(', ')}</span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-slate-600 text-xs text-center tabular-nums">
                        {license.expiration_date && license.expiration_date.trim() !== ''
                          && license.expiration_date !== '0000-00-00' && license.expiration_date !== '1900-01-01'
                          ? moment.utc(license.expiration_date).format('YYYY-MM-DD')
                          : '-'}
                      </td>
                      <td className="px-3 py-2.5 text-slate-600 text-xs text-center tabular-nums">
                        {license.renewal_date && license.renewal_date.trim() !== ''
                          && license.renewal_date !== '0000-00-00' && license.renewal_date !== '1900-01-01'
                          ? moment.utc(license.renewal_date).format('YYYY-MM-DD')
                          : '-'}
                      </td>
                      <td className="px-3 py-2.5 text-xs text-center tabular-nums">{daysDisplay}</td>
                      <td className="px-3 py-2.5 text-center">
                        <span className={`px-2 py-0.5 inline-flex text-[11px] leading-4 font-semibold rounded-full ${normalized.bg} ${normalized.text}`}>
                          {normalized.label}
                        </span>
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center justify-center space-x-0.5">
                          <button
                            onClick={() => setViewingLicense(license)}
                            className="p-1 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                            title="View"
                          >
                            <Eye className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => {
                              if (!isValidPositiveInteger(license.id)) {
                                console.error('[LicenseList] Edit blocked — license.id invalid:', license.id, license);
                                alert('❌ Cannot open edit: This license record has missing or invalid license_id. Please refresh the list and try again.');
                                return;
                              }
                              // —— Debug: selected record object (from the original DB-driven table row) ——
                              console.groupCollapsed('%c[LicenseList] Edit clicked → selected record object', 'color:#16a34a;font-weight:bold;');
                              console.log('license_id (primary key):', license.id);
                              console.log('selected record object:', license);
                              console.log('will navigate to:', `/license/edit/${license.id}`);
                              console.groupEnd();
                              saveLicenseState(); setLicenseFromPage(); navigate(`/license/edit/${license.id}`, { state: { from: location.pathname + location.search } });
                            }}
                            className="p-1 text-slate-500 hover:text-green-600 hover:bg-green-50 rounded transition-colors"
                            title="Edit"
                          >
                            <Edit2 className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => handleApplyExpense(license)}
                            className="p-1 text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 rounded transition-colors"
                            title="Apply Expense"
                          >
                            <Receipt className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
            </table>
          </div>
        )}

        {!showTrash && viewMode === 'report' && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <div className="text-xs text-blue-600 uppercase tracking-wider">Total Licenses</div>
                <div className="text-3xl font-bold text-blue-800 mt-1">{licenseReportStats.total}</div>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                <div className="text-xs text-red-600 uppercase tracking-wider">Expired</div>
                <div className="text-3xl font-bold text-red-800 mt-1">{licenseReportStats.expired}</div>
              </div>
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
                <div className="text-xs text-orange-600 uppercase tracking-wider">Expiring in 30 Days</div>
                <div className="text-3xl font-bold text-orange-800 mt-1">{licenseReportStats.expiring30}</div>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                <div className="text-xs text-green-600 uppercase tracking-wider">Valid</div>
                <div className="text-3xl font-bold text-green-800 mt-1">{licenseReportStats.valid}</div>
              </div>
            </div>

            {/* === Group Analysis (moved up) === */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="px-6 py-4 bg-gradient-to-r from-indigo-50 to-purple-50 border-b border-slate-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-indigo-600" />
                    Group Analysis
                  </h2>
                  <div className="flex items-center gap-4 text-sm">
                    <div className="px-3 py-1.5 bg-indigo-100 text-indigo-700 rounded-full font-medium">
                      {filteredGroupAnalysis.length} Groups
                    </div>
                    <div className="px-3 py-1.5 bg-green-100 text-green-700 rounded-full font-medium">
                      {filteredGroupAnalysis.reduce((sum, g) => sum + g.companyCount, 0)} Companies
                    </div>
                    <div className="px-3 py-1.5 bg-amber-100 text-amber-700 rounded-full font-medium">
                      {filteredGroupAnalysis.reduce((sum, g) => sum + g.licenseCount, 0)} Licenses
                    </div>
                  </div>
                </div>

                {/* === Filters === */}
                <div ref={gaFilterRef} className="flex items-center gap-2 mt-3 flex-wrap">
                  <Filter className="w-4 h-4 text-slate-400" />
                  {[
                    { key: 'groups', label: 'Group', options: gaAllGroups, filterKey: 'groups' },
                    { key: 'companies', label: 'Company', options: gaAvailableCompanies, filterKey: 'companies' },
                    { key: 'states', label: 'State', options: gaAvailableStates, filterKey: 'states' },
                    { key: 'categories', label: 'Category', options: gaAvailableCategories, filterKey: 'categories' },
                  ].map(({ key, label, options, filterKey }) => {
                    const selected = gaFilters[filterKey as keyof typeof gaFilters];
                    const isActive = activeGaFilter === key;
                    const count = selected.length;
                    return (
                      <div key={key} className="relative">
                        <button
                          onClick={() => setActiveGaFilter(isActive ? null : key)}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                            count > 0
                              ? 'bg-blue-50 border-blue-300 text-blue-700'
                              : 'bg-white border-slate-300 text-slate-600 hover:border-slate-400'
                          }`}
                        >
                          {label}
                          {count > 0 && (
                            <span className="ml-1 bg-blue-600 text-white rounded-full px-1.5 py-0.5 text-[10px]">
                              {count}
                            </span>
                          )}
                        </button>
                        {isActive && (
                          <div className="absolute top-full left-0 mt-1 w-56 bg-white border border-slate-200 rounded-lg shadow-lg z-50 py-1">
                            <div className="px-2 pb-1">
                              <input
                                type="text"
                                placeholder="Search..."
                                value={gaFilterSearch[key] || ''}
                                onChange={(e) => setGaFilterSearch(fs => ({ ...fs, [key]: e.target.value }))}
                                onClick={(e) => e.stopPropagation()}
                                className="w-full px-2 py-1 text-xs border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
                                autoFocus
                              />
                            </div>
                            <button
                              onClick={() => { setGaFilters(f => ({ ...f, [filterKey]: [] })); setGaFilterSearch(fs => ({ ...fs, [key]: '' })); setActiveGaFilter(null); }}
                              className="w-full flex items-center justify-between px-3 py-1 text-xs text-slate-500 hover:bg-slate-50 whitespace-nowrap"
                            >
                              <span>Clear</span>
                              {count > 0 && <span className="text-slate-400">{count} selected</span>}
                            </button>
                            <div className="border-t border-slate-100 my-1"></div>
                            <div className="max-h-48 overflow-y-auto">
                              {(() => {
                                const filtered = options.filter(v => !gaFilterSearch[key] || v.toLowerCase().includes(gaFilterSearch[key].toLowerCase()));
                                if (filtered.length === 0) return <div className="px-3 py-2 text-xs text-slate-400">No matches</div>;
                                return filtered.map(v => {
                                  const checked = selected.includes(v);
                                  return (
                                    <label key={v} className="flex items-center gap-2 px-3 py-1.5 text-xs cursor-pointer hover:bg-slate-50">
                                      <input type="checkbox" checked={checked} onChange={() => {
                                        setGaFilters(f => ({ ...f, [filterKey]: checked ? f[filterKey].filter(x => x !== v) : [...f[filterKey], v] }));
                                      }} className="w-3 h-3 rounded border-slate-300 text-blue-600 focus:ring-blue-500 flex-shrink-0" />
                                      <span className={checked ? 'text-blue-600 font-medium' : 'text-slate-700'}>{v}</span>
                                    </label>
                                  );
                                });
                              })()}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {(gaFilters.groups.length > 0 || gaFilters.companies.length > 0 || gaFilters.states.length > 0 || gaFilters.categories.length > 0) && (
                    <button
                      onClick={() => setGaFilters({ groups: [], companies: [], states: [], categories: [] })}
                      className="px-2 py-1.5 text-xs text-red-500 hover:text-red-700 font-medium"
                    >
                      Reset All
                    </button>
                  )}
                </div>
              </div>

              {filteredGroupAnalysis.length === 0 ? (
                <div className="px-6 py-12 text-center text-slate-500">
                  暫無有牌照的公司數據
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {filteredGroupAnalysis.map((group) => {
                    const isExpanded = expandedGroups.has(group.groupName);
                    return (
                      <div key={group.groupName} className="hover:bg-slate-50 transition-colors">
                        <button
                          onClick={() => {
                            const next = new Set(expandedGroups);
                            if (isExpanded) next.delete(group.groupName);
                            else next.add(group.groupName);
                            setExpandedGroups(next);
                          }}
                          className="w-full px-6 py-4 flex items-center justify-between text-left"
                        >
                          <div className="flex items-center gap-3">
                            {isExpanded ? (
                              <ChevronDown className="w-4 h-4 text-slate-500" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-slate-500" />
                            )}
                            <span className="text-lg font-bold text-slate-800">
                              {group.groupName}
                            </span>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="flex items-center gap-1.5 text-sm">
                              <Building2 className="w-3.5 h-3.5 text-indigo-500" />
                              <span className="text-slate-600">
                                {group.companyCount} {group.companyCount === 1 ? 'Company' : 'Companies'}
                              </span>
                            </div>
                            <div className="flex items-center gap-1.5 text-sm">
                              <FileText className="w-3.5 h-3.5 text-amber-500" />
                              <span className="text-slate-600">
                                {group.licenseCount} {group.licenseCount === 1 ? 'License' : 'Licenses'}
                              </span>
                            </div>
                          </div>
                        </button>

                        {isExpanded && (
                          <div className="px-6 pb-4 space-y-2">
                            {group.companies.map((company) => {
                              const companyKey = `${group.groupName}|${company.nickname}`;
                              const isCompanyExpanded = expandedCompanies.has(companyKey);
                              return (
                                <div
                                  key={company.nickname}
                                  className="bg-indigo-50 border border-indigo-100 rounded-lg"
                                >
                                  <button
                                    onClick={() => {
                                      const next = new Set(expandedCompanies);
                                      if (isCompanyExpanded) next.delete(companyKey);
                                      else next.add(companyKey);
                                      setExpandedCompanies(next);
                                    }}
                                    className="w-full px-4 py-3 flex items-center justify-between text-left"
                                  >
                                    <div className="flex items-center gap-3">
                                      {isCompanyExpanded ? (
                                        <ChevronDown className="w-4 h-4 text-slate-500" />
                                      ) : (
                                        <ChevronRight className="w-4 h-4 text-slate-500" />
                                      )}
                                      <div>
                                        <span className="text-base font-bold text-slate-800">
                                          {company.company_name}
                                        </span>
                                        <span className="ml-2 text-sm text-slate-500">
                                          ({company.nickname})
                                        </span>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                      <div className="flex flex-wrap gap-1">
                                        {company.states.length <= 8 ? (
                                          company.states.map((state, idx) => (
                                            <span key={idx} className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                                              {state}
                                            </span>
                                          ))
                                        ) : (
                                          <>
                                            {company.states.slice(0, 6).map((state, idx) => (
                                              <span key={idx} className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                                                {state}
                                              </span>
                                            ))}
                                            <span className="px-1.5 py-0.5 bg-slate-200 text-slate-600 rounded text-xs font-medium">
                                              +{company.states.length - 6}
                                            </span>
                                          </>
                                        )}
                                      </div>
                                      <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                                        {company.licenseCount} {company.licenseCount === 1 ? 'License' : 'Licenses'}
                                      </span>
                                    </div>
                                  </button>

                                  {isCompanyExpanded && (
                                    <div className="px-4 pb-4">
                                      {company.states.length > 8 && (
                                        <div className="mb-3 flex flex-wrap gap-1">
                                          {company.states.map((state, idx) => (
                                            <span key={idx} className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">
                                              {state}
                                            </span>
                                          ))}
                                        </div>
                                      )}

                                      <div className="overflow-x-auto">
                                        <table className="w-full text-sm">
                                          <thead>
                                            <tr className="border-b border-indigo-200 text-left text-xs text-slate-500">
                                              <th className="pb-2 pr-4 font-medium">Certificate</th>
                                              <th className="pb-2 pr-4 font-medium">Location</th>
                                              <th className="pb-2 pr-4 font-medium">License No.</th>
                                              <th className="pb-2 pr-4 font-medium">Required</th>
                                              <th className="pb-2 pr-4 font-medium">Status</th>
                                              <th className="pb-2 font-medium">Expiration</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {company.licenses.map((lic) => (
                                              <tr
                                                key={lic.id}
                                                className="border-b border-indigo-50 hover:bg-white"
                                              >
                                                <td className="py-2 pr-4 text-slate-800 font-medium">
                                                  {lic.certificate_name || '-'}
                                                </td>
                                                <td className="py-2 pr-4 text-slate-600">
                                                  {lic.location || '-'}
                                                </td>
                                                <td className="py-2 pr-4 text-slate-600 font-mono text-xs">
                                                  {lic.license_no || '-'}
                                                </td>
                                                <td className="py-2 pr-4 text-xs">
                                                  {lic.required_status
                                                    ? {
                                                        required: '必須有',
                                                        optional_on_demand: '可選，有需求就做',
                                                        optional_gov_contact: '可選，政府聯繫再做',
                                                        not_required_gov_decide: '不做，政府聯繫再決定',
                                                      }[lic.required_status] || lic.required_status
                                                    : '-'}
                                                </td>
                                                <td className="py-2 pr-4">
                                                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                                                    (lic.status || '').toLowerCase() === 'active'
                                                      ? 'bg-green-100 text-green-700'
                                                      : (lic.status || '').toLowerCase() === 'cancelled'
                                                      ? 'bg-red-100 text-red-700'
                                                      : 'bg-slate-100 text-slate-600'
                                                  }`}>
                                                    {lic.status || '-'}
                                                  </span>
                                                </td>
                                                <td className="py-2 text-slate-600">
                                                  {lic.expiration_date
                                                    ? String(lic.expiration_date).split('T')[0].split('-').join('/')
                                                    : '-'}
                                                </td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <SimplePieChart
                data={licenseReportStats.byStatus.map(s => ({ name: s.status, value: s.count }))}
                title="License Status Distribution"
              />
              <SimpleBarChart
                data={licenseReportStats.byCategory.slice(0, 8).map(c => ({ name: c.category, value: c.count }))}
                title="Licenses by Category"
              />
            </div>

            <SimpleBarChart
              data={licenseReportStats.byCompany.slice(0, 10).map(c => ({ name: c.company_name, value: c.count }))}
              title="Licenses by Company (Top 10)"
            />
          </div>
        )}
        
        {/* 分页控制 - 仅在非回收站模式下显示 */}
        {!showTrash && viewMode === 'list' && filteredLicenses.length > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-slate-600">{t('pagination.show')}</span>
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                className="px-2 py-1 border border-slate-300 rounded text-sm"
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={75}>75</option>
                <option value={100}>100</option>
              </select>
              <span className="text-sm text-slate-600">{t('pagination.perPage')}</span>
            </div>
            
            <div className="flex items-center space-x-2">
              <span className="text-sm text-slate-600">
                {t('pagination.showing')} {startIndex + 1}-{Math.min(endIndex, filteredLicenses.length)} {t('pagination.of')} {licenses.length} {t('pagination.records')}
                {filteredLicenses.length < licenses.length && (
                  <span className="text-slate-400 ml-1">({filteredLicenses.length} visible)</span>
                )}
              </span>
            </div>

            <div className="flex items-center space-x-1">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="px-2 py-1 text-sm border border-slate-300 rounded hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('pagination.first')}
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 text-sm border border-slate-300 rounded hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('pagination.prev')}
              </button>
              
              {/* 页码显示 */}
              <div className="flex items-center space-x-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-3 py-1 text-sm border rounded ${
                        currentPage === pageNum
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'border-slate-300 hover:bg-slate-100'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 text-sm border border-slate-300 rounded hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('pagination.next')}
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="px-2 py-1 text-sm border border-slate-300 rounded hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('pagination.last')}
              </button>
            </div>
          </div>
        )}
      </div>
      )}

      {/* License Detail Modal */}
      {viewingLicense && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-5xl w-full mx-4 max-h-[85vh] overflow-y-auto">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <div>
                <h2 className="text-lg font-bold text-slate-800">{t('license.licenseDetails')}</h2>
                <p className="text-xs text-slate-500 mt-0.5 font-mono">License ID: {viewingLicense.id ?? '-'}</p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => {
                    if (!viewingLicense || !isValidPositiveInteger(viewingLicense.id)) {
                      console.error('[LicenseList] Edit (modal) blocked — viewingLicense.id invalid:', viewingLicense?.id, viewingLicense);
                      alert('❌ Cannot open edit: This license record has missing or invalid license_id. Please refresh the list and try again.');
                      return;
                    }
                    saveLicenseState(); setLicenseFromPage(); navigate(`/license/edit/${viewingLicense.id}`, { state: { from: location.pathname + location.search } });
                  }}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
                >
                  <Edit2 className="w-3.5 h-3.5" />
                  {t('action.edit')}
                </button>
                <span className={`px-3 py-1 inline-flex text-sm leading-5 font-semibold rounded-full ${getNormalizedStatus(viewingLicense).bg} ${getNormalizedStatus(viewingLicense).text}`}>
                  {getNormalizedStatus(viewingLicense).label}
                </span>
                <button
                  onClick={() => setViewingLicense(null)}
                  className="text-slate-400 hover:text-slate-600 p-1 rounded hover:bg-slate-100 transition-colors"
                  aria-label="Close"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="p-6 space-y-6">

              {/* ========== Section 1: Basic Info ========== */}
              <div>
                <h3 className="text-sm font-bold text-slate-900 mb-3 pb-2 border-b-2 border-blue-200">1. Basic Information</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide">{t('license.company')}</label>
                    <p className="text-slate-800 mt-0.5 font-medium">{viewingLicense.company_name || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide">{t('license.certificateName')}</label>
                    <p className="text-slate-800 mt-0.5 font-medium">{viewingLicense.certificate_name || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide">{t('license.licenseNumber')}</label>
                    <p className="text-slate-800 mt-0.5 font-mono">{viewingLicense.license_no || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide">{t('license.state')}</label>
                    <p className="text-slate-800 mt-0.5">{viewingLicense.state || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide">{t('license.location')}</label>
                    <p className="text-slate-800 mt-0.5">{viewingLicense.location || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide">{t('license.level')}</label>
                    <p className="text-slate-800 mt-0.5">{viewingLicense.level || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide">{t('license.category')}</label>
                    <div className="flex flex-wrap gap-1 mt-0.5">
                      {(viewingLicense.category || '').split(',').map(s => s.trim()).filter(Boolean).length === 0 ? (
                        <span className="text-slate-400 text-sm">-</span>
                      ) : (viewingLicense.category || '').split(',').map(s => s.trim()).filter(Boolean).map((tag, i) => (
                        <span key={i} className="inline-block px-2 py-0.5 text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded-md">{tag}</span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide">{t('license.department')}</label>
                    <p className="text-slate-800 mt-0.5">{viewingLicense.department || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide">License Type</label>
                    <p className="text-slate-800 mt-0.5">{viewingLicense.license_type || viewingLicense.renewal_type || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide">{t('license.requiredStatus')}</label>
                    <p className="text-slate-800 mt-0.5">
                      {viewingLicense.required_status
                        ? {
                            required: '必須有',
                            optional_on_demand: '可選，有需求就做',
                            optional_gov_contact: '可選，政府聯繫再做',
                            not_required_gov_decide: '不做，政府聯繫再決定',
                          }[viewingLicense.required_status] || viewingLicense.required_status
                        : '-'}
                    </p>
                  </div>
                </div>
              </div>

              {/* ========== Section 2: Dates ========== */}
              <div>
                <h3 className="text-sm font-bold text-slate-900 mb-3 pb-2 border-b-2 border-green-200">2. Dates</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide">Registration / Issue Date</label>
                    <p className="text-slate-800 mt-0.5 font-mono">
                      {(() => {
                        const d = viewingLicense.registration_date || viewingLicense.issue_date;
                        return d ? moment(d).format('YYYY-MM-DD') : '-';
                      })()}
                    </p>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide">{t('license.expirationDate')}</label>
                    <p className="text-slate-800 mt-0.5 font-mono font-semibold text-orange-700">
                      {viewingLicense.expiration_date ? moment(viewingLicense.expiration_date).format('YYYY-MM-DD') : '-'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide">{t('license.renewalDate')}</label>
                    <p className="text-slate-800 mt-0.5 font-mono">
                      {viewingLicense.renewal_date && viewingLicense.renewal_date.trim() !== ''
                        && viewingLicense.renewal_date !== '0000-00-00' && viewingLicense.renewal_date !== '1900-01-01'
                        ? moment.utc(viewingLicense.renewal_date).format('YYYY-MM-DD')
                        : '-'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide">Renewal Type</label>
                    <p className="text-slate-800 mt-0.5">{viewingLicense.renewal_type || viewingLicense.renewal_cycle ? `${viewingLicense.renewal_type || '-'}${viewingLicense.renewal_cycle ? ` (${viewingLicense.renewal_cycle})` : ''}` : '-'}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide">Cancelled Date</label>
                    <p className="text-slate-800 mt-0.5 font-mono">
                      {viewingLicense.cancelled_date ? moment(viewingLicense.cancelled_date).format('YYYY-MM-DD') : '-'}
                    </p>
                  </div>
                </div>
              </div>

              {/* ========== Section 3: Renewal Workflow ========== */}
              <div>
                <h3 className="text-sm font-bold text-slate-900 mb-3 pb-2 border-b-2 border-amber-200">3. Renewal Workflow</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide">{t('license.renewalMethod')}</label>
                    <p className="text-slate-800 mt-0.5">{viewingLicense.renewal_method || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide">{t('license.renewalGuide')}</label>
                    <p className="text-slate-800 mt-0.5">{viewingLicense.renewal_guide || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide">{t('license.renewalInfo')}</label>
                    <div className="flex flex-wrap gap-1 mt-0.5">
                      {(viewingLicense.renewal_info || '').split(',').map(s => s.trim()).filter(Boolean).length === 0 ? (
                        <span className="text-slate-400 text-sm">-</span>
                      ) : (viewingLicense.renewal_info || '').split(',').map(s => s.trim()).filter(Boolean).map((tag, i) => (
                        <span key={i} className="inline-block px-2 py-0.5 text-xs bg-green-50 text-green-700 border border-green-200 rounded-md">{tag}</span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide">{t('license.infoPeriod')}</label>
                    <p className="text-slate-800 mt-0.5">{viewingLicense.info_period || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide">{t('license.infoProvider')}</label>
                    <p className="text-slate-800 mt-0.5">{viewingLicense.info_provider || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide">{t('license.noticeInvoice')}</label>
                    <p className="text-slate-800 mt-0.5">{viewingLicense.notice_invoice || '-'}</p>
                  </div>
                </div>
              </div>

              {/* ========== Section 4: Account & Credentials ========== */}
              <div>
                <h3 className="text-sm font-bold text-slate-900 mb-3 pb-2 border-b-2 border-purple-200">4. Account &amp; Credentials</h3>
                <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide">{t('license.website')}</label>
                    {viewingLicense.website ? (
                      <a href={viewingLicense.website.startsWith('http') ? viewingLicense.website : `https://${viewingLicense.website}`}
                         target="_blank" rel="noopener noreferrer"
                         className="text-blue-700 mt-0.5 hover:underline inline-block break-all">
                        {viewingLicense.website}
                      </a>
                    ) : <p className="text-slate-800 mt-0.5">-</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide">{t('license.account')}</label>
                    <p className="text-slate-800 mt-0.5 font-mono">{viewingLicense.account || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide">{t('license.passwordPin')}</label>
                    <p className="text-slate-800 mt-0.5 font-mono">{viewingLicense.password_pin || '-'}</p>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide">Questions</label>
                    <p className="text-slate-800 mt-0.5 whitespace-pre-wrap">{viewingLicense.questions || '-'}</p>
                  </div>
                </div>
              </div>

              {/* ========== Section 5: Contact ========== */}
              <div>
                <h3 className="text-sm font-bold text-slate-900 mb-3 pb-2 border-b-2 border-cyan-200">5. Contact Information</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide">{t('license.contactPerson')}</label>
                    <p className="text-slate-800 mt-0.5">{viewingLicense.contact_person || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide">{t('license.phoneNumber')}</label>
                    <p className="text-slate-800 mt-0.5 font-mono">{viewingLicense.phone_number || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide">{t('license.contactEmail')}</label>
                    <p className="text-slate-800 mt-0.5 break-all">{viewingLicense.contact_email || '-'}</p>
                  </div>
                </div>
              </div>

              {/* ========== Section 6: Fees ========== */}
              <div>
                <h3 className="text-sm font-bold text-slate-900 mb-3 pb-2 border-b-2 border-emerald-200">6. Fee Information</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide">Renewal Fee</label>
                    <p className="text-slate-800 mt-0.5 font-bold text-emerald-700">
                      {viewingLicense.renewal_fee !== null && viewingLicense.renewal_fee !== undefined && viewingLicense.renewal_fee !== ''
                        ? `$${Number(viewingLicense.renewal_fee).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                        : '-'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide">Conv. Fee</label>
                    <p className="text-slate-800 mt-0.5 font-bold text-emerald-700">
                      {viewingLicense.conv_fee !== null && viewingLicense.conv_fee !== undefined && viewingLicense.conv_fee !== ''
                        ? `$${Number(viewingLicense.conv_fee).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                        : '-'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide">Payment Method</label>
                    <p className="text-slate-800 mt-0.5">{viewingLicense.payment_method || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide">Quarter</label>
                    <p className="text-slate-800 mt-0.5">{viewingLicense.qtr || '-'}</p>
                  </div>
                  <div className="col-span-2 md:col-span-3">
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide">Fee Comment</label>
                    <p className="text-slate-800 mt-1 whitespace-pre-wrap bg-slate-50 p-2 rounded">{viewingLicense.fee_comment || '-'}</p>
                  </div>
                </div>
              </div>

              {/* ========== Section 7: Follow-up & Notes ========== */}
              <div>
                <h3 className="text-sm font-bold text-slate-900 mb-3 pb-2 border-b-2 border-pink-200">7. Follow-up &amp; Notes</h3>
                <div className="grid grid-cols-1 gap-y-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide">Follow-up Strategy</label>
                    <p className="text-slate-800 mt-0.5 whitespace-pre-wrap">{viewingLicense.follow_up || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide">Remark / Notes</label>
                    <p className="text-slate-800 mt-1 whitespace-pre-wrap bg-slate-50 p-2 rounded">
                      {viewingLicense.remark || viewingLicense.notes || '-'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
